import { QdrantService } from '../services/qdrantService';
import { generateNewPatternLoop, generateCustomPatternLoop, generateFromAlbum, fetchAndConcatenateAudio, MissingAudioError } from '../generators/patternLoopGenerator';
import type { LoopResult, GeneratorMode, SavedLoop } from '../generators/types';
import { STYLE_GROUPS, MAX_FILE_SIZE_MB } from '../config/app.config';
import { type SizeCategory, SECTION_ALIASES } from '../config/music.config';
import { track, incrementLoopCount, loopCount, sessionStartTime } from '../analytics/analyticsService';
import { AuthService, type User } from '../auth/authService';
import { LoopLibraryService, computeLoopId } from '../library/loopLibraryService';
import { UploadService, fileToBase64, type MyLibraryAlbum, type UploadedAlbum } from '../upload/uploadService';
import { encodeLoopToUrl, type SharePayload } from '../sharing/loopShare';

// Dev/Prod mode flag
const isDev = import.meta.env.DEV;

export class AudioLoopPlayer {
    private audioContext: AudioContext | null = null;
    private audioBuffer: AudioBuffer | null = null;
    private sourceNode: AudioBufferSourceNode | null = null;
    private analyserNode: AnalyserNode | null = null;
    private isPlaying: boolean = false;
    private currentLoop: LoopResult | null = null;
    private qdrantService: QdrantService;
    private authService: AuthService;
    private loopLibraryService: LoopLibraryService;
    private uploadService: UploadService;
    private currentUser: User | null = null;
    private currentLoopId: string | null = null; // deterministic hash of current loop
    private playbackRate: number = 1.0;
    private playbackStartTime: number = 0;
    private playbackOffset: number = 0;
    private playStartTime: number = 0;
    private animationFrameId: number | null = null;
    private resizeTimeout: number | null = null;
    private selectedStyleGroup: string = '';
    private selectedSizeCategory: SizeCategory = 'SHORT';

    // Song pattern overview
    private songPatternOverviewEl: HTMLElement | null = null;

    // Sequence strip
    private sequenceStripEl: HTMLElement | null = null;
    private seqTrackEl: HTMLElement | null = null;
    private sequenceBarTimings: Array<{ start: number; end: number; el: HTMLElement; centerX: number }> = [];
    private lastActiveBarIdx: number = -1;
    // Sliding window constants
    private static readonly SEQ_BAR_W = 10;   // px per bar block
    private static readonly SEQ_BAR_GAP = 2;  // px gap between bars (matches CSS gap: 2px)
    private static readonly SEQ_BLOCK_GAP = 3; // px gap between segment blocks (matches CSS gap: 3px)
    private static readonly SEQ_WINDOW_BARS = 16; // number of bars visible at once
    // Duct tape: repeat the pattern this many times so the strip scrolls forward
    // without needing wrap-around detection. At 50 repeats most users will never reach the end.
    private static readonly SEQ_REPEAT_COUNT = 50;

    // Canvas visualizer
    private canvas: HTMLCanvasElement;
    private canvasContext: CanvasRenderingContext2D;
    private tempCanvas: HTMLCanvasElement;
    private tempContext: CanvasRenderingContext2D;

    // UI Elements
    private lcdLoopNameEl: HTMLElement;
    private lcdStatusMsgEl: HTMLElement;
    private isLoadedFromLibrary: boolean = false;
    // BPM pulse interval for generate button glow
    private bpmPulseInterval: number | null = null;
    private genreSelect: HTMLSelectElement;
    private sizeShortBtn: HTMLButtonElement;
    private sizeMidBtn: HTMLButtonElement;
    private sizeLargeBtn: HTMLButtonElement;
    private sizeCustomBtn: HTMLButtonElement;
    private barPatternSection: HTMLElement;
    private generateBtn: HTMLButtonElement;
    private generateLabelEl: HTMLElement;
    private playBtn: HTMLButtonElement;
    private stopBtn: HTMLButtonElement;
    private increaseBtn: HTMLButtonElement;
    private decreaseBtn: HTMLButtonElement;

    private infoStatus: HTMLElement;
    private infoTrace: HTMLPreElement | null = null;

    // LCD style screen elements
    private lcdPrevBtn: HTMLButtonElement;
    private lcdNextBtn: HTMLButtonElement;
    private lcdStyleNameEl: HTMLElement;
    private lcdModeEl: HTMLElement;
    private lcdContentEl: HTMLElement;
    private lcdStyleNames: string[] = [];
    private currentStyleIndex: number = 0;

    // Auth UI elements
    private loginBtn: HTMLButtonElement;
    private userAvatar: HTMLElement;
    private userAvatarImg: HTMLImageElement;
    private userAvatarName: HTMLElement;
    private userDropdown: HTMLElement;
    private logoutBtn: HTMLButtonElement;
    private isRegisterMode: boolean = false;
    private saveNudgeDismissed: boolean = false;

    // Library UI elements
    private saveBtn: HTMLButtonElement;
    private myLoopsBtn: HTMLButtonElement;
    private libraryPanel: HTMLElement;
    private libraryOverlay: HTMLElement;
    private libraryList: HTMLElement;
    private libraryCloseBtn: HTMLButtonElement;

    // Upload UI elements
    private uploadBtn: HTMLButtonElement;
    private uploadPanel: HTMLElement;
    private uploadOverlay: HTMLElement;
    private uploadPanelCloseBtn: HTMLButtonElement;
    private uploadAlbumNameInput: HTMLInputElement;
    private uploadBpmInput: HTMLInputElement;
    private uploadTimeSigSelect: HTMLSelectElement;
    private uploadStyleGroupSelect: HTMLSelectElement;
    private uploadErrorMsg: HTMLElement;
    private uploadSuccessMsg: HTMLElement;
    private uploadSubmitBtn: HTMLButtonElement;
    private uploadSpinner: HTMLElement;
    // Per-token picker state: token abbreviation -> array of {file, isFill}
    private uploadPickerFiles: Record<string, { file: File; isFill: boolean }[]> = {};

    // My Albums UI elements + state
    private myAlbumsBtn: HTMLButtonElement;
    private myAlbumsPanel: HTMLElement;
    private myAlbumsOverlay: HTMLElement;
    private myAlbumsPanelCloseBtn: HTMLButtonElement;
    private myAlbumsList: HTMLElement;
    private myAlbumsStatusEl: HTMLElement;
    /** Currently pinned user album — when set, Generate uses generateFromAlbum() */
    private pinnedAlbum: MyLibraryAlbum | null = null;
    // Token type definitions (abbreviation + display label + genre aliases)
    private static readonly TOKEN_TYPES = [
        { token: 'verse',   label: 'Verse',   aliases: SECTION_ALIASES['verse']   ?? [] },
        { token: 'chorus',  label: 'Chorus',  aliases: SECTION_ALIASES['chorus']  ?? [] },
        { token: 'bridge',  label: 'Bridge',  aliases: SECTION_ALIASES['bridge']  ?? [] },
        { token: 'pre',     label: 'Pre',     aliases: SECTION_ALIASES['pre']     ?? [] },
        { token: 'intro',   label: 'Intro',   aliases: SECTION_ALIASES['intro']   ?? [] },
        { token: 'ending',  label: 'Ending',  aliases: SECTION_ALIASES['ending']  ?? [] },
    ] as const;

    private static readonly SIZE_CATEGORY_DISPLAY_NAMES: Record<string, string> = {
        SHORT:  'Standard',
        MID:    'Creative',
        LARGE:  'Crazy',
        CUSTOM: 'Custom',
    };

    private static readonly TOKEN_CLASS: Record<string, string> = {
        verse:   'verse',
        chorus:  'chorus',
        bridge:  'bridge',
        pre:     'pre',
        intro:   'intro',
        ending:  'ending',
    };

    private static readonly TOKEN_ABBR: Record<string, string> = {
        verse:   'Vs',
        chorus:  'Ch',
        bridge:  'Br',
        pre:     'Pre',
        intro:   'Intro',
        ending:  'End',
    };

    // Custom builder state
    /** User-built pattern: array of {section: bars} objects */
    private customPattern: Array<Record<string, number>> = [];
    /** Currently selected section type in the builder */
    private builderSelectedSection: string = 'verse';
    /** Currently selected bar count in the builder */
    private builderSelectedBars: number = 8;
    // Custom builder DOM elements
    private customBuilderOverlay: HTMLElement | null = null;
    private customBuilderSheet: HTMLElement | null = null;
    private customBuilderChips: HTMLElement | null = null;
    private customBuilderHint: HTMLElement | null = null;
    private customBuilderConfirmBtn: HTMLButtonElement | null = null;

    constructor() {
        this.qdrantService = new QdrantService();
        this.authService = new AuthService();
        this.loopLibraryService = new LoopLibraryService();
        this.uploadService = new UploadService();

        // Get canvas
        this.canvas = document.getElementById('visualizerCanvas') as HTMLCanvasElement;
        this.canvasContext = this.canvas.getContext('2d')!;
        
        // Initialize temp canvas for visualizer reflection
        this.tempCanvas = document.createElement('canvas');
        this.tempContext = this.tempCanvas.getContext('2d')!;
        
        this.initCanvas();

        // Get UI elements
        this.lcdLoopNameEl = document.getElementById('lcdLoopName')!;
        this.lcdStatusMsgEl = document.getElementById('lcdStatusMsg')!;
        this.genreSelect = document.getElementById('genreSelect') as HTMLSelectElement;
        this.sizeShortBtn = document.getElementById('sizeShortBtn') as HTMLButtonElement;
        this.sizeMidBtn = document.getElementById('sizeMidBtn') as HTMLButtonElement;
        this.sizeLargeBtn = document.getElementById('sizeLargeBtn') as HTMLButtonElement;
        this.sizeCustomBtn = document.getElementById('sizeCustomBtn') as HTMLButtonElement;
        this.barPatternSection = document.getElementById('barPatternSection')!;
        this.generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
        this.generateLabelEl = document.getElementById('generateLabel')!;
        this.playBtn = document.getElementById('playBtn') as HTMLButtonElement;
        this.stopBtn = document.getElementById('stopBtn') as HTMLButtonElement;
        this.increaseBtn = document.getElementById('increaseBtn') as HTMLButtonElement;
        this.decreaseBtn = document.getElementById('decreaseBtn') as HTMLButtonElement;
        this.infoStatus = document.getElementById('infoStatus')!;
        this.infoTrace = document.getElementById('infoTrace') as HTMLPreElement | null;

        // LCD style screen elements
        this.lcdPrevBtn = document.getElementById('lcdPrevBtn') as HTMLButtonElement;
        this.lcdNextBtn = document.getElementById('lcdNextBtn') as HTMLButtonElement;
        this.lcdStyleNameEl = document.getElementById('lcdStyleName')!;
        this.lcdModeEl = document.getElementById('lcdModeLabel')!;
        this.lcdContentEl = document.getElementById('lcdContent')!;

        // Auth UI elements
        this.loginBtn = document.getElementById('loginBtn') as HTMLButtonElement;
        this.userAvatar = document.getElementById('userAvatar')!;
        this.userAvatarImg = document.getElementById('userAvatarImg') as HTMLImageElement;
        this.userAvatarName = document.getElementById('userAvatarName')!;
        this.userDropdown = document.getElementById('userDropdown')!;
        this.logoutBtn = document.getElementById('logoutBtn') as HTMLButtonElement;

        // Library UI elements
        this.saveBtn = document.getElementById('saveBtn') as HTMLButtonElement;
        this.myLoopsBtn = document.getElementById('myLoopsBtn') as HTMLButtonElement;
        this.libraryPanel = document.getElementById('libraryPanel')!;
        this.libraryOverlay = document.getElementById('libraryOverlay')!;
        this.libraryList = document.getElementById('libraryList')!;
        this.libraryCloseBtn = document.getElementById('libraryCloseBtn') as HTMLButtonElement;

        // Upload UI elements
        this.uploadBtn = document.getElementById('uploadBtn') as HTMLButtonElement;
        this.uploadPanel = document.getElementById('uploadPanel')!;
        this.uploadOverlay = document.getElementById('uploadOverlay')!;
        this.uploadPanelCloseBtn = document.getElementById('uploadPanelCloseBtn') as HTMLButtonElement;
        this.uploadAlbumNameInput = document.getElementById('uploadAlbumName') as HTMLInputElement;
        this.uploadBpmInput = document.getElementById('uploadBpm') as HTMLInputElement;
        this.uploadTimeSigSelect = document.getElementById('uploadTimeSig') as HTMLSelectElement;
        this.uploadStyleGroupSelect = document.getElementById('uploadStyleGroup') as HTMLSelectElement;
        this.uploadErrorMsg = document.getElementById('uploadErrorMsg')!;
        this.uploadSuccessMsg = document.getElementById('uploadSuccessMsg')!;
        this.uploadSubmitBtn = document.getElementById('uploadSubmitBtn') as HTMLButtonElement;
        this.uploadSpinner = document.getElementById('uploadSpinner')!;

        // My Albums UI elements
        this.myAlbumsBtn = document.getElementById('myAlbumsBtn') as HTMLButtonElement;
        this.myAlbumsPanel = document.getElementById('myAlbumsPanel')!;
        this.myAlbumsOverlay = document.getElementById('myAlbumsOverlay')!;
        this.myAlbumsPanelCloseBtn = document.getElementById('myAlbumsPanelCloseBtn') as HTMLButtonElement;
        this.myAlbumsList = document.getElementById('myAlbumsList')!;
        this.myAlbumsStatusEl = document.getElementById('myAlbumsStatus')!;

        // Song pattern overview
        this.songPatternOverviewEl = document.getElementById('songPatternOverview');

        // Sequence strip
        this.sequenceStripEl = document.getElementById('sequenceStrip');

        // Custom builder DOM elements
        this.customBuilderOverlay = document.getElementById('customBuilderOverlay');
        this.customBuilderSheet = document.getElementById('customBuilderSheet');
        this.customBuilderChips = document.getElementById('customBuilderChips');
        this.customBuilderHint = document.getElementById('customBuilderHint');
        this.customBuilderConfirmBtn = document.getElementById('customBuilderConfirmBtn') as HTMLButtonElement | null;

        // Initialize per-token picker state and wire up each picker
        for (const { token } of AudioLoopPlayer.TOKEN_TYPES) {
            this.uploadPickerFiles[token] = [];
        }
        this.initTokenPickers();
        this.initUploadStyleSelect();

        // Theme toggle — restore saved theme and wire up button
        const themeToggleBtn = document.getElementById('themeToggleBtn') as HTMLButtonElement;
        const savedTheme = localStorage.getItem('theme') || 'dark';
        document.documentElement.dataset.theme = savedTheme;
        this.updateThemeIcon(themeToggleBtn, savedTheme);
        themeToggleBtn.addEventListener('click', () => {
            const current = document.documentElement.dataset.theme || 'light';
            const next = current === 'dark' ? 'light' : 'dark';
            document.documentElement.dataset.theme = next;
            localStorage.setItem('theme', next);
            this.updateThemeIcon(themeToggleBtn, next);
        });

        // Bind event listeners
        this.genreSelect.addEventListener('change', () => this.onGenreChange());
        this.generateBtn.addEventListener('click', () => {
            this.unlockAudioContext();
            this.generateNewLoop();
        });
        this.playBtn.addEventListener('click', () => {
            this.unlockAudioContext();
            this.togglePlayPause();
        });
        this.stopBtn.addEventListener('click', () => this.stop());
        this.increaseBtn.addEventListener('click', () => this.increaseRate());
        this.decreaseBtn.addEventListener('click', () => this.decreaseRate());

        // Auth event listeners
        this.loginBtn.addEventListener('click', () => this.openAuthModal());
        this.userAvatar.addEventListener('click', () => this.toggleUserDropdown());
        this.logoutBtn.addEventListener('click', () => this.handleLogout());

        // Save nudge event listeners
        document.getElementById('saveNudgeSignInBtn')!.addEventListener('click', () => this.openAuthModal('save'));
        document.getElementById('saveNudgeDismissBtn')!.addEventListener('click', () => {
            this.saveNudgeDismissed = true;
            this.hideSaveNudge();
        });

        // Auth modal event listeners
        document.getElementById('authModalClose')!.addEventListener('click', () => this.closeAuthModal());
        document.getElementById('authModal')!.addEventListener('click', (e) => {
            if (e.target === e.currentTarget) this.closeAuthModal();
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this.closeAuthModal();
        });
        document.getElementById('authGoogleBtn')!.addEventListener('click', () => {
            this.closeAuthModal();
            this.authService.login();
        });
        document.getElementById('authToggleBtn')!.addEventListener('click', () => {
            this.setAuthModalMode(!this.isRegisterMode);
        });
        document.getElementById('authEmailForm')!.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = (document.getElementById('authEmailInput') as HTMLInputElement).value.trim();
            const password = (document.getElementById('authPasswordInput') as HTMLInputElement).value;
            const submitBtn = document.getElementById('authSubmitBtn') as HTMLButtonElement;
            submitBtn.disabled = true;

            const result = this.isRegisterMode
                ? await this.authService.registerWithEmail(email, password)
                : await this.authService.loginWithEmail(email, password);

            submitBtn.disabled = false;

            if (result.ok) {
                this.closeAuthModal();
                const user = this.authService.getUser();
                if (user) {
                    this.currentUser = user;
                    this.renderLoggedIn(user);
                    track('user_identified', { name: user.name }, user.id);
                }
            } else {
                this.showAuthModalError(result.error ?? 'Something went wrong');
            }
        });

        // Library event listeners
        this.saveBtn.addEventListener('click', () => this.handleSaveLoop());
        this.myLoopsBtn.addEventListener('click', () => this.openLibrary());
        this.libraryCloseBtn.addEventListener('click', () => this.closeLibrary());
        this.libraryOverlay.addEventListener('click', () => this.closeLibrary());

        // Upload event listeners
        this.uploadBtn.addEventListener('click', () => this.handleUploadBtnClick());
        this.uploadPanelCloseBtn.addEventListener('click', () => this.closeUploadPanel());
        this.uploadOverlay.addEventListener('click', () => this.closeUploadPanel());
        this.uploadAlbumNameInput.addEventListener('input', () => this.updateUploadSubmitState());
        this.uploadBpmInput.addEventListener('input', () => this.updateUploadSubmitState());
        this.uploadStyleGroupSelect.addEventListener('change', () => this.updateUploadSubmitState());
        this.uploadSubmitBtn.addEventListener('click', () => this.handleUploadSubmit());

        // My Albums event listeners
        this.myAlbumsBtn.addEventListener('click', () => this.openMyAlbumsPanel());
        this.myAlbumsPanelCloseBtn.addEventListener('click', () => this.closeMyAlbumsPanel());
        this.myAlbumsOverlay.addEventListener('click', () => this.closeMyAlbumsPanel());

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.userAvatar.contains(e.target as Node)) {
                this.userDropdown.classList.remove('visible');
            }
        });
        
        // Size button event listeners
        this.sizeShortBtn.addEventListener('click', () => this.onSizeChange('SHORT'));
        this.sizeMidBtn.addEventListener('click', () => this.onSizeChange('MID'));
        this.sizeLargeBtn.addEventListener('click', () => this.onSizeChange('LARGE'));
        this.sizeCustomBtn.addEventListener('click', () => this.openCustomBuilder());

        // LCD nav button event listeners
        this.lcdPrevBtn.addEventListener('click', () => this.navigateLcd('prev'));
        this.lcdNextBtn.addEventListener('click', () => this.navigateLcd('next'));

        // Ensure barPatternSection is visible on startup (always in Advanced mode)
        this.barPatternSection.style.display = 'block';
        
        // Handle window resize with debounce (canvas only — carousel handled by ResizeObserver)
        window.addEventListener('resize', () => {
            if (this.resizeTimeout !== null) {
                clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = window.setTimeout(() => {
                this.resizeTimeout = null;
                this.initCanvas();
            }, 100);
        });

        // Button active state management
        const buttons = [this.playBtn, this.stopBtn, this.increaseBtn, this.decreaseBtn, this.generateBtn];
        buttons.forEach(btn => {
            btn.addEventListener('click', () => {
                buttons.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
            });
        });

        // Show info panel only in dev mode (hidden by default via CSS to prevent flash)
        if (isDev) {
            const infoPanel = document.querySelector('.info') as HTMLElement;
            if (infoPanel) {
                infoPanel.classList.add('dev-visible');
            }
        }

        // Load style groups on startup
        this.loadStyleGroups();

        // Ensure all playback controls start in the correct disabled state
        this.setPlayingState(false);

        // Track session start (anonymous — user may not be loaded yet)
        track('session_start', {
            referrer: document.referrer || 'direct',
            user_agent: navigator.userAgent,
        });

        // Track session end on tab hide/close (user_id included if logged in)
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                track('session_end', {
                    duration_seconds: (Date.now() - sessionStartTime) / 1000,
                    loops_generated: loopCount,
                }, this.currentUser?.id);
            }
        });

        // Restore session on page load (silent — 401 = logged out, no error shown)
        this.initAuth();
    }

    // ---------------------------------------------------------------------------
    // Auth methods
    // ---------------------------------------------------------------------------

    private async initAuth(): Promise<void> {
        const user = await this.authService.init();
        if (user) {
            this.currentUser = user;
            this.renderLoggedIn(user);
            // Fire user_identified to link this anonymous session to the logged-in user.
            // session_start fires before initAuth() resolves (constructor is sync),
            // so this event bridges the anonymous session_id with the id.
            track('user_identified', { name: user.name }, user.id);
            // Pre-load the library cache so "already saved" detection works immediately
            this.loopLibraryService.listLoops().catch(() => {
                // Silently ignore — library will load on first open
            });
        }
        // If null: silently stay logged out — no error shown
    }

    private renderLoggedIn(user: User): void {
        this.loginBtn.style.display = 'none';

        // Avatar: show picture for Google users, initial letter for email users
        if (user.picture_url) {
            this.userAvatarImg.src = user.picture_url;
            this.userAvatarImg.alt = user.name;
            this.userAvatarImg.style.display = 'block';
            const initial = document.getElementById('userAvatarInitial');
            if (initial) initial.style.display = 'none';
        } else {
            this.userAvatarImg.style.display = 'none';
            const initial = document.getElementById('userAvatarInitial');
            if (initial) {
                initial.textContent = (user.name || user.email || '?')[0].toUpperCase();
                initial.style.display = 'flex';
            }
        }

        // Name is shown in the dropdown, not inline (compact avatar-only button)
        this.userAvatarName.textContent = user.name;
        const dropdownName = document.getElementById('userDropdownName');
        if (dropdownName) dropdownName.textContent = user.name;

        // Founding Member badge — show only for the first 100 users
        const foundingBadge = document.getElementById('foundingBadge');
        if (foundingBadge) {
            foundingBadge.style.display = user.is_founding_member ? 'block' : 'none';
        }

        this.userAvatar.style.display = 'flex';
        this.myLoopsBtn.style.display = 'flex';
        this.uploadBtn.style.display = 'flex';
        // Always show My Albums when logged in so users can see pending/processing albums
        this.myAlbumsBtn.style.display = 'flex';
        // Update save button visibility if a loop is already loaded
        this.updateSaveBtnVisibility();
        this.hideSaveNudge();
    }

    private renderLoggedOut(): void {
        this.loginBtn.style.display = 'flex';
        this.userAvatar.style.display = 'none';
        this.userDropdown.classList.remove('visible');
        this.myLoopsBtn.style.display = 'none';
        this.uploadBtn.style.display = 'none';
        this.myAlbumsBtn.style.display = 'none';
        // Save button stays visible but inactive when logged out
        this.saveBtn.disabled = true;
        this.saveBtn.classList.add('inactive');
        this.loopLibraryService.clearCache();
        // Close upload panel if open
        this.closeUploadPanel();
        // Clear any pinned album when logging out
        this.unpinAlbum();
    }

    private openAuthModal(context?: 'save'): void {
        const modal = document.getElementById('authModal')!;
        modal.style.display = 'flex';
        this.setAuthModalMode(false);
        const ctx = document.getElementById('authModalContext')!;
        ctx.style.display = context === 'save' ? 'block' : 'none';
        (document.getElementById('authEmailInput') as HTMLInputElement).focus();
    }

    private closeAuthModal(): void {
        const modal = document.getElementById('authModal')!;
        modal.style.display = 'none';
        this.clearAuthModalError();
    }

    private setAuthModalMode(register: boolean): void {
        this.isRegisterMode = register;
        (document.getElementById('authModalTitle') as HTMLElement).textContent = register ? 'Create Account' : 'Sign In';
        (document.getElementById('authSubmitBtn') as HTMLButtonElement).textContent = register ? 'Create Account' : 'Sign In';
        (document.getElementById('authToggleText') as HTMLElement).textContent = register ? 'Already have an account?' : "Don't have an account?";
        (document.getElementById('authToggleBtn') as HTMLButtonElement).textContent = register ? 'Sign In' : 'Create Account';
        const hint = document.getElementById('authPasswordHint')!;
        hint.style.display = register ? 'block' : 'none';
        const pwInput = document.getElementById('authPasswordInput') as HTMLInputElement;
        pwInput.autocomplete = register ? 'new-password' : 'current-password';
        this.clearAuthModalError();
    }

    private showAuthModalError(msg: string): void {
        const el = document.getElementById('authErrorMsg')!;
        el.textContent = msg;
        el.style.display = 'block';
    }

    private clearAuthModalError(): void {
        const el = document.getElementById('authErrorMsg')!;
        el.textContent = '';
        el.style.display = 'none';
    }

    private toggleUserDropdown(): void {
        this.userDropdown.classList.toggle('visible');
    }

    private async handleLogout(): Promise<void> {
        await this.authService.logout();
        this.currentUser = null;
        this.currentLoopId = null;
        this.renderLoggedOut();
    }

    // ---------------------------------------------------------------------------
    // Save nudge helpers
    // ---------------------------------------------------------------------------

    private showSaveNudge(): void {
        if (this.saveNudgeDismissed) return;
        document.getElementById('saveNudge')!.style.display = 'flex';
    }

    private hideSaveNudge(): void {
        document.getElementById('saveNudge')!.style.display = 'none';
    }

    // ---------------------------------------------------------------------------
    // Save button helpers
    // ---------------------------------------------------------------------------

    private updateSaveBtn(): void {
        const alreadySaved = !!(this.currentLoopId &&
            (this.isLoadedFromLibrary || this.loopLibraryService.isSaved(this.currentLoopId)));
        const canSave = !!this.currentLoop && !alreadySaved;

        this.saveBtn.disabled = !canSave;
        this.saveBtn.classList.toggle('inactive', !canSave);
        this.saveBtn.classList.toggle('saved', alreadySaved);
        this.saveBtn.classList.toggle('disabled-saved', alreadySaved);
        this.saveBtn.title = alreadySaved ? 'Already saved' : 'Save loop';
    }

    /** @deprecated use updateSaveBtn */
    private updateSaveBtnVisibility(): void { this.updateSaveBtn(); }
    /** @deprecated use updateSaveBtn */
    private updateSaveBtnState(): void { this.updateSaveBtn(); }

    private async handleSaveLoop(): Promise<void> {
        if (!this.currentUser) {
            this.openAuthModal('save');
            return;
        }
        if (!this.currentLoop) return;
        if (this.saveBtn.classList.contains('saving')) return;
        if (this.currentLoopId && this.loopLibraryService.isSaved(this.currentLoopId)) return;

        this.saveBtn.classList.add('saving');

        try {
            const tokens = this.currentLoop.segments.map(s => s.file);
            const durations = this.currentLoop.segments.map(s => s.duration);
            const tokenTypes = this.currentLoop.segments.map(s => s.token);

            const loopId = await this.loopLibraryService.saveLoop({
                album: this.currentLoop.album,
                genre: this.currentLoop.genre,
                bpm: this.currentLoop.bpm,
                style_group: this.selectedStyleGroup,
                bar_size: this.selectedSizeCategory,
                bars: this.currentLoop.segments.length,
                tokens,
                durations,
                token_types: tokenTypes,
                slots: this.currentLoop.slots ?? [],
                total_duration: this.currentLoop.totalDuration,
                custom_pattern: this.selectedSizeCategory === 'CUSTOM' && this.customPattern.length > 0
                    ? this.customPattern
                    : undefined,
                pulse_compas: this.currentLoop.pulse_compas ?? 4,
            });

            this.currentLoopId = loopId;

            // Track successful save
            track('loop_saved', {
                style_group: this.selectedStyleGroup,
                bar_size: this.selectedSizeCategory,
                bpm: this.currentLoop.bpm,
                total_duration: this.currentLoop.totalDuration,
                loop_id: loopId,
            }, this.currentUser.id);

            this.updateSaveBtn();
        } catch (err) {
            if (isDev) { console.error('[Library] Save failed:', err); }
        } finally {
            this.saveBtn.classList.remove('saving');
        }
    }

    // ---------------------------------------------------------------------------
    // Library panel
    // ---------------------------------------------------------------------------

    private async openLibrary(): Promise<void> {
        this.libraryPanel.classList.add('open');
        this.libraryOverlay.classList.add('visible');
        this.libraryList.innerHTML = '<div class="library-loading">Loading...</div>';

        try {
            const loops = await this.loopLibraryService.listLoops();
            this.renderLibraryList(loops);
        } catch (err) {
            if (isDev) { console.error('[Library] Failed to load loops:', err); }
            this.libraryList.innerHTML = '<div class="library-empty">Could not load loops. Please try again.</div>';
        }
    }

    private closeLibrary(): void {
        this.libraryPanel.classList.remove('open');
        this.libraryOverlay.classList.remove('visible');
    }

    // ---------------------------------------------------------------------------
    // Share modal
    // ---------------------------------------------------------------------------

    /**
     * Open the share modal, populate the URL input, and wire up the copy button.
     * The modal shows the full shareable URL so the user can see and copy it.
     *
     * Ticket 20260408b
     */
    private openShareModal(url: string): void {
        const backdrop = document.getElementById('shareModalBackdrop') as HTMLElement;
        const urlInput = document.getElementById('shareUrlInput') as HTMLInputElement;
        const copyBtn = document.getElementById('shareUrlCopyBtn') as HTMLButtonElement;
        const closeBtn = document.getElementById('shareModalCloseBtn') as HTMLButtonElement;

        urlInput.value = url;
        backdrop.classList.add('visible');

        // Select all text in the input for easy manual copy
        setTimeout(() => urlInput.select(), 50);

        const close = () => {
            backdrop.classList.remove('visible');
            copyBtn.innerHTML = '<i class="fas fa-copy"></i>';
            copyBtn.classList.remove('copied');
            copyBtn.onclick = null;
            closeBtn.onclick = null;
            backdrop.onclick = null;
        };

        copyBtn.onclick = async () => {
            try {
                await navigator.clipboard.writeText(url);
            } catch {
                // Fallback for browsers without clipboard API
                urlInput.select();
                document.execCommand('copy');
            }
            copyBtn.innerHTML = '<i class="fas fa-check"></i>';
            copyBtn.classList.add('copied');
            setTimeout(() => {
                copyBtn.innerHTML = '<i class="fas fa-copy"></i>';
                copyBtn.classList.remove('copied');
            }, 2000);
        };

        closeBtn.onclick = close;
        backdrop.onclick = (e) => {
            if (e.target === backdrop) close();
        };
    }

    // ---------------------------------------------------------------------------
    // Upload panel
    // ---------------------------------------------------------------------------

    /**
     * Populate the upload style <select> with <optgroup> + <option> elements
     * derived from STYLE_GROUPS. Each option value is an atomic genre string
     * (e.g. 'jazz'), not a group name, so the factory can match it directly.
     */
    private initUploadStyleSelect(): void {
        const select = this.uploadStyleGroupSelect;
        // Remove any existing options except the placeholder
        while (select.options.length > 1) {
            select.remove(1);
        }
        for (const [groupName, atomicGenres] of Object.entries(STYLE_GROUPS)) {
            const optgroup = document.createElement('optgroup');
            optgroup.label = groupName;
            for (const genre of atomicGenres) {
                const option = document.createElement('option');
                option.value = genre;
                // Capitalise first letter for display
                option.textContent = genre.charAt(0).toUpperCase() + genre.slice(1);
                optgroup.appendChild(option);
            }
            select.appendChild(optgroup);
        }
    }

    /** Wire up each per-token picker: Add button triggers hidden file input */
    private initTokenPickers(): void {
        const pickerEls = document.querySelectorAll<HTMLElement>('.token-picker');
        for (const pickerEl of pickerEls) {
            const token = pickerEl.dataset.token!;
            const addBtn = pickerEl.querySelector<HTMLButtonElement>('.token-picker-add-btn')!;
            const fileInput = pickerEl.querySelector<HTMLInputElement>('input[type="file"]')!;
            const fileList = pickerEl.querySelector<HTMLElement>('.token-picker-file-list')!;
            const errorEl = pickerEl.querySelector<HTMLElement>('.token-picker-error')!;

            addBtn.addEventListener('click', () => fileInput.click());

            fileInput.addEventListener('change', () => {
                const newFiles = Array.from(fileInput.files ?? []) as File[];
                fileInput.value = ''; // reset so same file can be re-added

                // Check total file count across all pickers (limit is tier-based)
                const maxUploadFiles = this.currentUser?.tier === 'pro' ? 60 : 40;
                const currentTotal = Object.values(this.uploadPickerFiles).reduce((s, arr) => s + arr.length, 0);
                if (currentTotal + newFiles.length > maxUploadFiles) {
                    errorEl.textContent = `Maximum ${maxUploadFiles} files total across all sections.`;
                    errorEl.style.display = 'block';
                    return;
                }

                const errors: string[] = [];
                const validFiles: File[] = [];
                for (const file of newFiles) {
                    const sizeMb = file.size / (1024 * 1024);
                    if (sizeMb > MAX_FILE_SIZE_MB) {
                        errors.push(`"${file.name}" exceeds ${MAX_FILE_SIZE_MB} MB (${sizeMb.toFixed(1)} MB).`);
                        continue;
                    }
                    validFiles.push(file);
                }

                if (errors.length > 0) {
                    errorEl.textContent = errors.join(' ');
                    errorEl.style.display = 'block';
                } else {
                    errorEl.style.display = 'none';
                    errorEl.textContent = '';
                }

                for (const file of validFiles) {
                    const entry = { file, isFill: false };
                    this.uploadPickerFiles[token].push(entry);
                    this.renderTokenPickerFileItem(entry, token, fileList);
                }

                this.updateUploadSubmitState();
            });
        }
    }

    /** Render a single file row inside a token picker's file list */
    private renderTokenPickerFileItem(
        entry: { file: File; isFill: boolean },
        token: string,
        fileList: HTMLElement,
    ): void {
        const { file } = entry;
        const item = document.createElement('div');
        item.className = 'upload-file-item';

        const sizekb = (file.size / 1024).toFixed(1);
        const fillCheckId = `fill-${token}-${file.name}-${Date.now()}`;
        item.innerHTML = `
            <span class="upload-file-name" title="${file.name}">${file.name}</span>
            <span class="upload-file-size">${sizekb} KB</span>
            <label class="upload-file-fill-label" title="Check if this sample contains a fill">
                <input type="checkbox" class="upload-file-fill-check" id="${fillCheckId}" />
                fill
            </label>
            <button type="button" class="upload-file-remove" title="Remove file"><i class="fas fa-times"></i></button>
        `;

        const fillCheck = item.querySelector('.upload-file-fill-check') as HTMLInputElement;
        fillCheck.addEventListener('change', () => {
            entry.isFill = fillCheck.checked;
        });

        const removeBtn = item.querySelector('.upload-file-remove') as HTMLButtonElement;
        removeBtn.addEventListener('click', () => {
            const idx = this.uploadPickerFiles[token].indexOf(entry);
            if (idx >= 0) {
                this.uploadPickerFiles[token].splice(idx, 1);
            }
            item.remove();
            this.updateUploadSubmitState();
        });

        fileList.appendChild(item);
    }


    // ---------------------------------------------------------------------------
    // My Albums panel
    // ---------------------------------------------------------------------------

    private openMyAlbumsPanel(): void {
        this.myAlbumsPanel.classList.add('open');
        this.myAlbumsOverlay.classList.add('visible');
        this.loadMyAlbums();
    }

    private closeMyAlbumsPanel(): void {
        this.myAlbumsPanel.classList.remove('open');
        this.myAlbumsOverlay.classList.remove('visible');
    }

    /** Pin an album: generator will use it exclusively until unpinned. */
    private pinAlbum(album: MyLibraryAlbum): void {
        this.pinnedAlbum = album;
        // Update LCD style name to reflect the pinned album
        const displayName = `MY: ${album.album_name.toUpperCase()}`;
        this.triggerLcdRefresh(displayName);
        // Update the genre select to match the album's genre (for UI consistency)
        // and show a pinned indicator in the top bar
        this.updateMyAlbumsBtnState();
    }

    /** Remove the pinned album and restore normal style-group generation. */
    private unpinAlbum(): void {
        if (!this.pinnedAlbum) return;
        this.pinnedAlbum = null;
        // Restore LCD to the current style group name
        const currentStyleName = this.lcdStyleNames[this.currentStyleIndex] ?? '';
        if (currentStyleName) this.triggerLcdRefresh(currentStyleName);
        this.updateMyAlbumsBtnState();
    }

    /** Show/hide the pinned album indicator badge in the top bar. */
    private updateMyAlbumsBtnState(): void {
        if (this.pinnedAlbum) {
            this.myAlbumsBtn.classList.add('pinned');
        } else {
            this.myAlbumsBtn.classList.remove('pinned');
        }
    }

    private async loadMyAlbums(): Promise<void> {
        this.myAlbumsList.innerHTML = '';
        this.myAlbumsStatusEl.textContent = 'Loading...';
        this.myAlbumsStatusEl.style.display = 'block';

        try {
            // Fetch both lists in parallel:
            // - allUploads: every album the user has ever submitted (staged, processing, approved, rejected)
            // - libraryAlbums: only albums that are fully processed and ready to generate from
            const [allUploads, libraryAlbums] = await Promise.all([
                this.uploadService.listUploads(),
                this.uploadService.getMyLibraryAlbums(),
            ]);

            this.myAlbumsStatusEl.style.display = 'none';

            // Build a lookup map of approved album_ids -> libraryAlbum for quick access
            const approvedMap = new Map<string, MyLibraryAlbum>(
                libraryAlbums.map(a => [a.album_id, a])
            );

            // Filter out rejected albums — show only active (staged / processing / approved)
            const activeUploads = allUploads.filter(
                (u: UploadedAlbum) => u.status !== 'rejected'
            );

            if (activeUploads.length === 0) {
                this.myAlbumsStatusEl.textContent = 'No albums yet. Upload some samples to get started.';
                this.myAlbumsStatusEl.style.display = 'block';
                return;
            }

            for (const upload of activeUploads) {
                const isApproved = approvedMap.has(upload.album_id);
                const libraryAlbum = approvedMap.get(upload.album_id);

                const card = document.createElement('div');
                const isPinned = isApproved && this.pinnedAlbum?.album_id === upload.album_id;
                card.className = `my-album-card${isPinned ? ' pinned' : ''}${!isApproved ? ' processing' : ''}`;

                const statusBadge = isApproved
                    ? '<span class="album-status-badge album-status-ready">Ready</span>'
                    : '<span class="album-status-badge album-status-processing">Processing...</span>';

                card.innerHTML = `
                    <div class="my-album-card-header">
                        <span class="my-album-name">${upload.album_name}</span>
                        ${statusBadge}
                    </div>
                    <div class="my-album-meta">
                        <span>${upload.genre.charAt(0).toUpperCase() + upload.genre.slice(1)}</span>
                        <span>${upload.bpm} BPM</span>
                    </div>
                `;

                // Only approved albums are clickable (pin / unpin)
                if (isApproved && libraryAlbum) {
                    card.addEventListener('click', () => {
                        if (this.pinnedAlbum?.album_id === libraryAlbum.album_id) {
                            this.unpinAlbum();
                            card.classList.remove('pinned');
                        } else {
                            const prevCard = this.myAlbumsList.querySelector('.my-album-card.pinned') as HTMLElement | null;
                            if (prevCard) prevCard.classList.remove('pinned');
                            this.pinAlbum(libraryAlbum);
                            card.classList.add('pinned');
                            this.closeMyAlbumsPanel();
                        }
                    });
                } else {
                    // Non-approved cards show a tooltip explaining their status
                    card.title = 'This album is still being processed. It will be available soon.';
                    card.style.cursor = 'default';
                }

                this.myAlbumsList.appendChild(card);
            }
        } catch (err) {
            this.myAlbumsStatusEl.textContent = 'Failed to load albums. Please try again.';
            this.myAlbumsStatusEl.style.display = 'block';
            if (isDev) { console.error('[MyAlbums] Error loading albums:', err); }
        }
    }

    /** Handle upload button click: TOS check then open panel */
    private async handleUploadBtnClick(): Promise<void> {
        const accepted = await showTosModal();
        if (!accepted) return;
        this.openUploadPanel();
    }

    private openUploadPanel(): void {
        this.uploadPanel.classList.add('open');
        this.uploadOverlay.classList.add('visible');
        // Reset form state
        this.resetUploadForm();
    }

    private closeUploadPanel(): void {
        this.uploadPanel.classList.remove('open');
        this.uploadOverlay.classList.remove('visible');
    }

    private resetUploadForm(): void {
        this.uploadAlbumNameInput.value = '';
        this.uploadBpmInput.value = '';
        this.uploadStyleGroupSelect.value = '';
        // Clear all per-token picker files and their rendered lists
        for (const { token } of AudioLoopPlayer.TOKEN_TYPES) {
            this.uploadPickerFiles[token] = [];
        }
        const pickerEls = document.querySelectorAll<HTMLElement>('.token-picker');
        for (const pickerEl of pickerEls) {
            const fileList = pickerEl.querySelector<HTMLElement>('.token-picker-file-list')!;
            const errorEl = pickerEl.querySelector<HTMLElement>('.token-picker-error')!;
            fileList.innerHTML = '';
            errorEl.style.display = 'none';
            errorEl.textContent = '';
        }
        this.uploadErrorMsg.textContent = '';
        this.uploadErrorMsg.classList.remove('visible');
        this.uploadSuccessMsg.textContent = '';
        this.uploadSuccessMsg.classList.remove('visible');
        this.uploadSubmitBtn.disabled = true;
    }

    /** Enable/disable submit button based on form validity */
    private updateUploadSubmitState(): void {
        const albumName = this.uploadAlbumNameInput.value.trim();
        const bpm = parseInt(this.uploadBpmInput.value, 10);
        const styleGroup = this.uploadStyleGroupSelect.value;
        const totalFiles = Object.values(this.uploadPickerFiles).reduce((s, arr) => s + arr.length, 0);

        const valid =
            albumName.length > 0 &&
            albumName.length <= 50 &&
            !isNaN(bpm) && bpm >= 60 && bpm <= 200 &&
            styleGroup !== '' &&
            totalFiles > 0;

        this.uploadSubmitBtn.disabled = !valid;
    }

    private showUploadError(msg: string): void {
        this.uploadErrorMsg.textContent = msg;
        this.uploadErrorMsg.classList.add('visible');
        this.uploadSuccessMsg.classList.remove('visible');
    }

    private clearUploadError(): void {
        this.uploadErrorMsg.textContent = '';
        this.uploadErrorMsg.classList.remove('visible');
    }

    /** Show confirmation dialog before upload. Returns true if user confirmed. */
    private showUploadConfirmModal(albumName: string, bpm: number, styleGroup: string): Promise<boolean> {
        const backdrop = document.getElementById('uploadConfirmBackdrop') as HTMLElement;
        const summaryEl = document.getElementById('uploadConfirmSummary') as HTMLElement;
        const cancelBtn = document.getElementById('uploadConfirmCancelBtn') as HTMLButtonElement;
        const proceedBtn = document.getElementById('uploadConfirmProceedBtn') as HTMLButtonElement;

        // Build summary HTML
        const tokenLines = AudioLoopPlayer.TOKEN_TYPES
            .map(({ token, label }) => {
                const count = this.uploadPickerFiles[token].length;
                return `<span>${label}: <strong>${count} file${count !== 1 ? 's' : ''}</strong></span>`;
            })
            .join('<br>');

        const totalFiles = Object.values(this.uploadPickerFiles).reduce((s, arr) => s + arr.length, 0);

        summaryEl.innerHTML = `
            <strong>${albumName}</strong><br>
            ${bpm} BPM &middot; ${styleGroup.charAt(0).toUpperCase() + styleGroup.slice(1)}<br><br>
            ${tokenLines}<br><br>
            Total: <strong>${totalFiles} file${totalFiles !== 1 ? 's' : ''}</strong>
        `;

        backdrop.classList.add('visible');

        return new Promise<boolean>((resolve) => {
            const cleanup = () => {
                backdrop.classList.remove('visible');
                cancelBtn.onclick = null;
                proceedBtn.onclick = null;
            };

            cancelBtn.onclick = () => { cleanup(); resolve(false); };
            proceedBtn.onclick = () => { cleanup(); resolve(true); };
        });
    }

    /** Handle upload form submission */
    private async handleUploadSubmit(): Promise<void> {
        if (this.uploadSubmitBtn.disabled) return;

        // Gather form values
        const albumName = this.uploadAlbumNameInput.value.trim();
        const bpm = parseInt(this.uploadBpmInput.value, 10);
        const styleGroup = this.uploadStyleGroupSelect.value;
        // pulse_compas: "3_6_8" means 6/8 displayed but stored as 3 (same math as 3/4)
        const timeSigRaw = this.uploadTimeSigSelect.value;
        const pulseCompas = timeSigRaw === '3_6_8' ? 3 : parseInt(timeSigRaw, 10);

        // Show confirmation dialog first
        const confirmed = await this.showUploadConfirmModal(albumName, bpm, styleGroup);
        if (!confirmed) return;

        // Show loading state
        this.uploadSubmitBtn.disabled = true;
        this.clearUploadError();
        this.uploadSuccessMsg.classList.remove('visible');

        try {
            // Convert files to base64 grouped by token type
            this.uploadSubmitBtn.textContent = '';
            this.uploadSpinner.classList.add('visible');
            this.uploadSubmitBtn.appendChild(this.uploadSpinner);

            const uploadFiles = [];
            for (const { token } of AudioLoopPlayer.TOKEN_TYPES) {
                for (const entry of this.uploadPickerFiles[token]) {
                    const audioData = await fileToBase64(entry.file);
                    uploadFiles.push({
                        file: entry.file.name,
                        token,
                        audio_data: audioData,
                        sample_rate: 44100, // default; server will detect actual rate
                        duration: 0,        // server will compute
                        is_fill: entry.isFill,
                    });
                }
            }

            await this.uploadService.uploadAlbum({
                album_name: albumName,
                genre: styleGroup,
                bpm,
                pulse_compas: pulseCompas,
                tos_accepted: true,
                files: uploadFiles,
            });

            // Track successful upload (compute file count before clearing picker state)
            const totalUploadedFiles = Object.values(this.uploadPickerFiles).reduce((s, arr) => s + arr.length, 0);
            track('sample_uploaded', {
                style_group: styleGroup,
                bpm,
                album_name: albumName,
                file_count: totalUploadedFiles,
            }, this.currentUser?.id);

            // Success — State 1: clear confirmation message in panel
            this.uploadSuccessMsg.textContent = 'Album received — being processed. Open My Albums to track its status.';
            this.uploadSuccessMsg.classList.add('visible');
            // Clear picker state
            for (const { token } of AudioLoopPlayer.TOKEN_TYPES) {
                this.uploadPickerFiles[token] = [];
            }
            const pickerEls = document.querySelectorAll<HTMLElement>('.token-picker');
            for (const pickerEl of pickerEls) {
                pickerEl.querySelector<HTMLElement>('.token-picker-file-list')!.innerHTML = '';
            }
            this.uploadAlbumNameInput.value = '';
            this.uploadBpmInput.value = '';
            this.uploadTimeSigSelect.value = '4';
            this.uploadStyleGroupSelect.value = '';

            if (isDev) {
                console.log('[Upload] Album submitted successfully:', albumName);
            }

            // Close panel after delay to let user read the confirmation
            setTimeout(() => this.closeUploadPanel(), 4000);

        } catch (err) {
            if (isDev) { console.error('[Upload] Submit failed:', err); }
            this.showUploadError(`Upload failed: ${(err as Error).message}`);
        } finally {
            this.uploadSpinner.classList.remove('visible');
            this.uploadSubmitBtn.disabled = false;
            this.uploadSubmitBtn.innerHTML = '<span class="upload-spinner" id="uploadSpinner"></span>Upload Album';
            // Re-bind spinner reference after innerHTML reset
            this.uploadSpinner = this.uploadSubmitBtn.querySelector('.upload-spinner')!;
        }
    }

    private renderLibraryList(loops: SavedLoop[]): void {
        if (loops.length === 0) {
            this.libraryList.innerHTML = '<div class="library-empty">No saved loops yet.<br>Generate one and save it!</div>';
            return;
        }

        this.libraryList.innerHTML = '';
        for (const loop of loops) {
            const item = document.createElement('div');
            item.className = 'loop-item';
            item.dataset.loopId = loop.loop_id;

            const savedDate = new Date(loop.saved_at).toLocaleDateString(undefined, {
                month: 'short', day: 'numeric', year: 'numeric'
            });

            item.innerHTML = `
                <div class="loop-item-header">
                    <span class="loop-item-style">${loop.style_group}</span>
                    <div class="loop-item-actions">
                        <button class="loop-item-share" title="Copy share link" data-loop-id="${loop.loop_id}">
                            <i class="fas fa-link"></i>
                        </button>
                        <button class="loop-item-delete" title="Delete" data-loop-id="${loop.loop_id}">
                            <i class="fas fa-trash-alt"></i>
                        </button>
                    </div>
                </div>
                <div class="loop-item-meta">
                    <span>${AudioLoopPlayer.SIZE_CATEGORY_DISPLAY_NAMES[loop.bar_size] ?? loop.bar_size}</span>
                    <span>${loop.bpm} BPM</span>
                    <span>${loop.total_duration.toFixed(1)}s</span>
                </div>
                <div class="loop-item-date">${savedDate}</div>
            `;

            // Click on item body (not action buttons) — load and play
            item.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                if (target.closest('.loop-item-delete') || target.closest('.loop-item-share')) return;
                this.loadSavedLoop(loop, item);
            });

            // Share button — open share modal with the URL visible
            const shareBtn = item.querySelector('.loop-item-share') as HTMLButtonElement;
            shareBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const url = encodeLoopToUrl(loop);
                this.openShareModal(url);
            });

            // Delete button
            const deleteBtn = item.querySelector('.loop-item-delete') as HTMLButtonElement;
            deleteBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.deleteSavedLoop(loop.loop_id, item);
            });

            this.libraryList.appendChild(item);
        }
    }

    /** LCD message for a failed library/share load: specific for missing samples, generic otherwise. */
    private loadErrorMessage(err: unknown, fallback: string): string {
        return err instanceof MissingAudioError
            ? 'Some samples in this loop are no longer available.'
            : fallback;
    }

    private async loadSavedLoop(savedLoop: SavedLoop, itemEl: HTMLElement): Promise<void> {
        if (itemEl.classList.contains('loading')) return;
        itemEl.classList.add('loading');

        try {
            if (this.isPlaying) {
                this.stop();
            }

            this.setLcdLoadingState(true);
            this.updateStatus('Loading loop from library...', 'loading');

            const { audioData, sampleRate } = await fetchAndConcatenateAudio(
                savedLoop.tokens,
                savedLoop.album,
                this.qdrantService
            );

            const savedTokenTypes = savedLoop.token_types ?? [];

            this.currentLoop = {
                audioData,
                album: savedLoop.album,
                genre: savedLoop.genre,
                bpm: savedLoop.bpm,
                pulse_compas: savedLoop.pulse_compas ?? 4,
                files: savedLoop.tokens,
                segments: savedLoop.tokens.map((file, i) => ({
                    file,
                    token: savedTokenTypes[i] ?? '',
                    duration: savedLoop.durations[i] ?? 0,
                })),
                slots: savedLoop.slots.length > 0 ? savedLoop.slots : undefined,
                totalDuration: savedLoop.total_duration,
                mode: 'Advanced',
                sampleRate,
            };

            this.currentLoopId = savedLoop.loop_id;
            this.onSizeChange(savedLoop.bar_size as SizeCategory);
            // Restore custom pattern when loading a CUSTOM loop so re-generate works
            if (savedLoop.bar_size === 'CUSTOM' && savedLoop.custom_pattern && savedLoop.custom_pattern.length > 0) {
                this.customPattern = savedLoop.custom_pattern;
                this._updateCustomBtnBadge();
            }
            const styleIndex = this.lcdStyleNames.indexOf(savedLoop.style_group);
            if (styleIndex >= 0) {
                this.currentStyleIndex = styleIndex;
                this.onLcdStyleChange(savedLoop.style_group);
            } else {
                this.selectedStyleGroup = savedLoop.style_group;
                this.genreSelect.value = savedLoop.style_group;
            }
            this.playbackRate = 1.0;
            this.playbackOffset = 0;
            this.updateTempoDisplay();

            // Clear old audio buffer
            this.audioBuffer = null;

            this.isLoadedFromLibrary = true;
            this.updateInfo(this.currentLoop, savedLoop.total_duration);
            this.renderSongPatternOverview(this.currentLoop);
            this.renderSequenceStrip(this.currentLoop);
            this.updateStatusLoopName();
            this.setPlayingState(false); // enables Play button now that currentLoop is set
            this.updateSaveBtnVisibility();
            this.updateSaveBtnState();

            // Close the library panel and autoplay
            this.closeLibrary();
            this.play();

            await new Promise(resolve => setTimeout(resolve, 80));
            if (this.audioContext && this.audioContext.state === 'suspended') {
                this.setPlayingState(false);
                this.updateStatus('Loop ready — tap Play to start', 'idle');
            }
        } catch (err) {
            if (isDev) { console.error('[Library] Failed to load saved loop:', err); }
            this.updateStatus(this.loadErrorMessage(err, 'Could not load loop. Please try again.'), 'error');
        } finally {
            this.setLcdLoadingState(false);
            itemEl.classList.remove('loading');
        }
    }

    /**
     * Load and auto-play a loop from a shared URL payload.
     * Called from main.ts when a #loop=... hash is detected on page load.
     * Does not require the user to be logged in.
     *
     * Ticket 20260408b
     */
    /** Called from main.ts when the URL has a #loop= hash that fails validation. Ticket 20260923b. */
    showInvalidShareLink(): void {
        this.updateStatus('This share link is invalid', 'error');
    }

    async loadSharedLoop(payload: SharePayload): Promise<void> {
        try {
            this.setLcdLoadingState(true);
            this.updateStatus('Loading shared loop...', 'loading');

            const { audioData, sampleRate } = await fetchAndConcatenateAudio(
                payload.tokens,
                payload.album,
                this.qdrantService
            );

            this.currentLoop = {
                audioData,
                album: payload.album,
                genre: payload.genre,
                bpm: payload.bpm,
                pulse_compas: payload.pulse_compas ?? 4,
                files: payload.tokens,
                segments: payload.tokens.map((file, i) => ({
                    file,
                    token: payload.token_types?.[i] ?? '',
                    duration: payload.durations?.[i] ?? 0,
                })),
                slots: (payload.slots && payload.slots.length > 0) ? payload.slots : undefined,
                totalDuration: audioData.length / sampleRate,
                mode: 'Advanced',
                sampleRate,
            };

            this.currentLoopId = null;
            this.playbackRate = 1.0;
            this.playbackOffset = 0;
            this.updateTempoDisplay();
            this.audioBuffer = null;
            this.isLoadedFromLibrary = false;

            // Restore style/size UI state from payload
            const styleIndex = this.lcdStyleNames.indexOf(payload.style_group);
            if (styleIndex >= 0) {
                this.currentStyleIndex = styleIndex;
                this.onLcdStyleChange(payload.style_group);
            } else {
                this.selectedStyleGroup = payload.style_group;
            }
            this.onSizeChange(payload.bar_size as SizeCategory);
            // Restore custom pattern when loading a shared CUSTOM loop
            if (payload.bar_size === 'CUSTOM' && payload.custom_pattern && payload.custom_pattern.length > 0) {
                this.customPattern = payload.custom_pattern;
                this._updateCustomBtnBadge();
            }

            this.updateInfo(this.currentLoop!, this.currentLoop!.totalDuration);
            this.renderSongPatternOverview(this.currentLoop!);
            this.renderSequenceStrip(this.currentLoop!);
            this.updateStatusLoopName();
            this.setPlayingState(false); // enables Play button now that currentLoop is set
            this.updateStatus('Shared loop ready — tap Play to start', 'idle');
            this.updateSaveBtnVisibility();
            this.updateSaveBtnState();
        } catch (err) {
            if (isDev) { console.error('[Share] Failed to load shared loop:', err); }
            this.updateStatus(this.loadErrorMessage(err, 'Could not load shared loop. Please try again.'), 'error');
        } finally {
            this.setLcdLoadingState(false);
        }
    }

    private async deleteSavedLoop(loopId: string, itemEl: HTMLElement): Promise<void> {
        // Ask for confirmation before deleting
        if (!window.confirm('Delete this saved loop?')) return;

        try {
            await this.loopLibraryService.deleteLoop(loopId);
            itemEl.remove();

            // If library list is now empty, show empty state
            if (this.libraryList.children.length === 0) {
                this.libraryList.innerHTML = '<div class="library-empty">No saved loops yet.<br>Generate one and save it!</div>';
            }

            // Update save button if the deleted loop was the current one
            if (this.currentLoopId === loopId) {
                this.updateSaveBtnState();
            }
        } catch (err) {
            if (isDev) { console.error('[Library] Failed to delete loop:', err); }
        }
    }

    // ---------------------------------------------------------------------------
    // Canvas / visualizer
    // ---------------------------------------------------------------------------

    private initCanvas() {
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = window.innerWidth * dpr;
        this.canvas.height = window.innerHeight * dpr;
        this.canvasContext.scale(dpr, dpr);
        
        // Update temp canvas dimensions for visualizer reflection
        this.tempCanvas.width = this.canvas.width;
        this.tempCanvas.height = this.canvas.height * 0.75;
    }

    private updateStatus(message: string, type: 'idle' | 'loading' | 'playing' | 'error' = 'idle') {
        if (type === 'playing' && this.currentLoop) {
            this.lcdStatusMsgEl.textContent = `${message} ${this.playbackRate.toFixed(2)}x`;
        } else {
            this.lcdStatusMsgEl.textContent = message;
        }
        this.infoStatus.textContent = type.charAt(0).toUpperCase() + type.slice(1);

        this.lcdStatusMsgEl.classList.toggle('playing', type === 'playing');

        const lcdScreen = document.getElementById('lcdScreen');
        if (lcdScreen) {
            lcdScreen.classList.toggle('lcd-error', type === 'error');
        }

        if (type === 'playing' && this.currentLoop) {
            this.startBpmPulse(this.currentLoop.bpm);
        } else {
            this.stopBpmPulse();
        }
    }

    private setLcdLoadingState(loading: boolean): void {
        if (loading) {
            this.lcdStyleNameEl.textContent = 'LOADING....';
            this.lcdContentEl.classList.add('lcd-loading');
        } else {
            const styleName = this.lcdStyleNames[this.currentStyleIndex] ?? this.selectedStyleGroup;
            this.lcdStyleNameEl.textContent = styleName;
            this.lcdContentEl.classList.remove('lcd-loading');
        }
    }

    private updateStatusLoopName(): void {
        if (this.currentLoop) {
            this.lcdLoopNameEl.textContent = `${this.currentLoop.bpm} BPM`;
        } else {
            this.lcdLoopNameEl.textContent = '';
        }
    }

    /** Start BPM-synced glow pulse on the generate button */
    private startBpmPulse(bpm: number): void {
        this.stopBpmPulse();
        const intervalMs = (60 / bpm) * 1000;
        this.bpmPulseInterval = window.setInterval(() => {
            this.generateBtn.classList.remove('glow');
            // Force reflow so animation restarts
            void this.generateBtn.offsetWidth;
            this.generateBtn.classList.add('glow');
        }, intervalMs);
    }

    /** Stop BPM pulse and remove glow class */
    private stopBpmPulse(): void {
        if (this.bpmPulseInterval !== null) {
            clearInterval(this.bpmPulseInterval);
            this.bpmPulseInterval = null;
        }
        this.generateBtn.classList.remove('glow');
    }

    private updateInfo(loop: LoopResult, duration: number) {
        if (!isDev || !this.infoTrace) return;

        const trace = loop.trace;
        if (!trace) {
            // Fallback: show basic info when no trace is available (e.g. library-loaded loops)
            const lines: string[] = [
                `ALBUM: ${loop.album}`,
                `GENRE: ${loop.genre}`,
                `BPM: ${loop.bpm}`,
                `MODE: ${loop.mode}`,
                `DURATION: ${duration.toFixed(2)}s`,
                ``,
                `SEGMENTS: ${loop.segments.length}`,
            ];
            for (let i = 0; i < loop.segments.length; i++) {
                const s = loop.segments[i];
                lines.push(`  ${i + 1}. (${s.token}) ${s.file}`);
            }
            this.infoTrace.textContent = lines.join('\n');
            return;
        }

        // Build full generation decision chain text
        const lines: string[] = [
            `ALBUM: ${trace.album}`,
            `GENRE: ${trace.genre}`,
            `BPM: ${trace.bpm}`,
            ``,
            `SONG PATTERN (${trace.mode}):`,
        ];
        for (const p of trace.pattern) {
            lines.push(`  ${p.token}:  ${p.bars} bars`);
        }
        lines.push(``);

        for (let si = 0; si < trace.slots.length; si++) {
            const slot = trace.slots[si];
            lines.push(`SLOT ${si + 1} — ${slot.token}: ${slot.targetBars} bars`);

            // Show all samples available for this token (grouped by bar size)
            if (slot.samplesAvailable.length > 0) {
                // Group by bar size for compact display
                const bySize = new Map<number, string[]>();
                for (const s of slot.samplesAvailable) {
                    const existing = bySize.get(s.bars);
                    if (existing) existing.push(s.file);
                    else bySize.set(s.bars, [s.file]);
                }
                const sizeEntries = Array.from(bySize.entries()).sort((a, b) => a[0] - b[0]);
                lines.push(`  samples (${slot.samplesAvailable.length} total):`);
                for (const [size, files] of sizeEntries) {
                    lines.push(`    ${size} bars: [${files.join(', ')}]`);
                }
            }

            lines.push(`  combo: [${slot.sizeCombo.join(', ')}]`);
            for (let pi = 0; pi < slot.positions.length; pi++) {
                const pos = slot.positions[pi];
                const available = pos.availableFiles.length > 0
                    ? `${pos.availableFiles.length} available [${pos.availableFiles.join(', ')}]`
                    : `selected`;
                lines.push(`  pos ${pi + 1} (${pos.size} bars): ${available} -> ${pos.selectedFile}`);
            }
            lines.push(``);
        }

        lines.push(`attempts: ${trace.attempts}  fallback: ${trace.fallbackUsed}`);

        // FINAL AUDIO SEQUENCE — the exact ordered file list sent to Qdrant and concatenated
        const barTimeSec = (60 / loop.bpm) * 4;
        lines.push(``);
        lines.push(`FINAL AUDIO SEQUENCE (${loop.segments.length} files):`);
        let cumulativeDuration = 0;
        for (let i = 0; i < loop.segments.length; i++) {
            const seg = loop.segments[i];
            const bars = Math.round((seg.duration / barTimeSec) * 100) / 100;
            cumulativeDuration += seg.duration;
            lines.push(`  ${(i + 1).toString().padStart(2)}. ${seg.file.padEnd(30)} (${seg.token}, ${bars} bars, ${seg.duration.toFixed(2)}s)`);
        }
        lines.push(`  total: ${cumulativeDuration.toFixed(2)}s`);

        this.infoTrace.textContent = lines.join('\n');
    }

    private updateTempoDisplay() {
        // Update LCD status message with new rate if playing
        if (this.isPlaying) {
            this.lcdStatusMsgEl.textContent = `Playing audio loop at ${this.playbackRate.toFixed(2)}x`;
        }
    }

    // ---------------------------------------------------------------------------
    // Song pattern overview
    // ---------------------------------------------------------------------------

    /**
     * Render the song pattern overview from a LoopResult.
     * Shows one colored block per song section (from loop.slots) with the section
     * name abbreviation and bar count number inside. Uses the same color tokens
     * as the sequence strip (.seq-verse, .seq-chorus, etc.).
     * Hidden when no loop is loaded; shown after generation or library load.
     */
    private renderSongPatternOverview(loop: LoopResult): void {
        if (!this.songPatternOverviewEl) return;

        this.songPatternOverviewEl.innerHTML = '';

        const sections: Array<{ token: string; duration: number }> =
            (loop.slots && loop.slots.length > 0)
                ? loop.slots
                : (loop.segments ?? []);

        if (sections.length === 0) {
            this.songPatternOverviewEl.style.display = 'none';
            return;
        }

        const barTime = (60 / loop.bpm) * 4;

        for (const section of sections) {
            const bars = Math.max(1, Math.round(section.duration / barTime));
            const tokenClass = AudioLoopPlayer.TOKEN_CLASS[section.token] ?? 'unknown';

            const block = document.createElement('div');
            block.className = `spo-block seq-${tokenClass}`;

            const label = document.createElement('span');
            label.className = 'spo-label';
            label.textContent = AudioLoopPlayer.TOKEN_ABBR[section.token] ?? section.token;

            const barsEl = document.createElement('span');
            barsEl.className = 'spo-bars';
            barsEl.textContent = String(bars);

            block.appendChild(label);
            block.appendChild(barsEl);
            this.songPatternOverviewEl.appendChild(block);
        }

        this.songPatternOverviewEl.style.display = 'flex';
    }

    // ---------------------------------------------------------------------------
    // Sequence strip
    // ---------------------------------------------------------------------------

    /**
     * Render the sequence strip from a LoopResult.
     * Renders from loop.slots (song structure sections) when available,
     * falling back to loop.segments (individual sample files) for legacy loops.
     * Builds one colored block per section, subdivided into fixed-width bar sub-blocks.
     * All blocks live inside a .seq-track div that slides via translateX (sliding window).
     * Pre-computes sequenceBarTimings (including centerX) for zero-cost per-frame tracking.
     */
    private renderSequenceStrip(loop: LoopResult): void {
        if (!this.sequenceStripEl) return;

        // Clear previous content and reset state
        this.sequenceStripEl.innerHTML = '';
        this.seqTrackEl = null;
        this.sequenceBarTimings = [];
        this.lastActiveBarIdx = -1;

        // Use slots (song structure) when available; fall back to segments (sample files)
        const baseSections: Array<{ token: string; duration: number }> =
            (loop.slots && loop.slots.length > 0)
                ? loop.slots
                : (loop.segments ?? []);

        if (baseSections.length === 0) {
            this.sequenceStripEl.style.display = 'none';
            return;
        }

        const BAR_W = AudioLoopPlayer.SEQ_BAR_W;
        const BAR_GAP = AudioLoopPlayer.SEQ_BAR_GAP;
        const BLOCK_GAP = AudioLoopPlayer.SEQ_BLOCK_GAP;
        const REPEAT = AudioLoopPlayer.SEQ_REPEAT_COUNT;

        // barTime in seconds (4/4 time)
        const barTime = (60 / loop.bpm) * 4;

        // Duration of one full loop pass (used to offset timings per repetition)
        const loopDuration = baseSections.reduce((sum, s) => sum + s.duration, 0);

        // Map token name to CSS class suffix — uses AudioLoopPlayer.TOKEN_CLASS

        // Create the scrollable inner track
        const track = document.createElement('div');
        track.className = 'seq-track';
        this.seqTrackEl = track;

        // trackX tracks the current X offset as we build blocks, for pre-computing centerX
        let trackX = 0;

        // Repeat the pattern REPEAT times so the strip scrolls forward indefinitely
        // without needing wrap-around detection. rawPos (unclamped) is used for lookup.
        for (let rep = 0; rep < REPEAT; rep++) {
            const repTimeOffset = rep * loopDuration;

            for (let si = 0; si < baseSections.length; si++) {
                const section = baseSections[si];
                const tokenClass = AudioLoopPlayer.TOKEN_CLASS[section.token] ?? 'unknown';
                const barsInSection = Math.max(1, Math.round(section.duration / barTime));
                const barDuration = section.duration / barsInSection;

                // Block width = sum of bar widths + gaps between bars
                const blockWidth = barsInSection * BAR_W + (barsInSection - 1) * BAR_GAP;

                // Create block element
                const block = document.createElement('div');
                block.className = `seq-block seq-${tokenClass}`;
                block.style.width = `${blockWidth}px`;

                // Label — use abbreviated name to keep blocks compact
                const label = document.createElement('span');
                label.className = 'seq-label';
                label.textContent = AudioLoopPlayer.TOKEN_ABBR[section.token] ?? section.token;
                block.appendChild(label);

                // Bars row
                const barsRow = document.createElement('div');
                barsRow.className = 'seq-bars-row';

                // Cumulative time within this repetition (before this section)
                const sectionTimeInRep = baseSections
                    .slice(0, si)
                    .reduce((sum, s) => sum + s.duration, 0);

                for (let b = 0; b < barsInSection; b++) {
                    const barEl = document.createElement('div');
                    barEl.className = 'seq-bar';
                    barEl.style.width = `${BAR_W}px`;

                    const barStart = repTimeOffset + sectionTimeInRep + b * barDuration;
                    const barEnd = barStart + barDuration;
                    // Pre-compute center X of this bar in track coordinates
                    const centerX = trackX + b * (BAR_W + BAR_GAP) + BAR_W / 2;

                    // Store timing + DOM ref + centerX for fast per-frame lookup
                    this.sequenceBarTimings.push({ start: barStart, end: barEnd, el: barEl, centerX });

                    barsRow.appendChild(barEl);
                }

                block.appendChild(barsRow);
                track.appendChild(block);

                // Advance trackX by block width + block gap
                // Add gap between blocks within a rep, and between reps (treat as continuous)
                const isLastBlockInRep = si === baseSections.length - 1;
                const isLastRep = rep === REPEAT - 1;
                if (!isLastBlockInRep || !isLastRep) {
                    trackX += blockWidth + BLOCK_GAP;
                } else {
                    trackX += blockWidth;
                }
            }
        }

        // Store total track width for clamping in updateSequenceHighlight
        (track as HTMLElement & { _totalWidth?: number })._totalWidth = trackX;

        this.sequenceStripEl.appendChild(track);
        this.sequenceStripEl.style.display = 'block';
    }

    /**
     * Update the active bar highlight based on current audio position.
     * Called inside the draw() rAF loop — O(n) scan, DOM only touched on bar change.
     * When the active bar changes, slides the track so the active bar stays centered
     * within the visible window.
     *
     * Uses rawPos (unclamped playback position) directly — the strip is pre-rendered
     * SEQ_REPEAT_COUNT times so rawPos always falls within a valid bar range without
     * any wrap-around detection needed.
     */
    private updateSequenceHighlight(): void {
        if (!this.audioContext || !this.audioBuffer || this.sequenceBarTimings.length === 0) return;

        const elapsed = this.audioContext.currentTime - this.playbackStartTime;
        // Use rawPos (unclamped) — matches the pre-rendered 50x repeated timings
        const rawPos = this.playbackOffset + elapsed * this.playbackRate;

        // Binary search — timings are sorted by start; O(log n) even with 50x repeat
        let lo = 0;
        let hi = this.sequenceBarTimings.length - 1;
        let newIdx = -1;
        while (lo <= hi) {
            const mid = (lo + hi) >>> 1;
            const t = this.sequenceBarTimings[mid];
            if (rawPos < t.start) {
                hi = mid - 1;
            } else if (rawPos >= t.end) {
                lo = mid + 1;
            } else {
                newIdx = mid;
                break;
            }
        }

        // Only touch DOM when active bar changes
        if (newIdx !== this.lastActiveBarIdx) {
            if (this.lastActiveBarIdx >= 0 && this.lastActiveBarIdx < this.sequenceBarTimings.length) {
                this.sequenceBarTimings[this.lastActiveBarIdx].el.classList.remove('active');
            }
            if (newIdx >= 0) {
                this.sequenceBarTimings[newIdx].el.classList.add('active');
            }
            this.lastActiveBarIdx = newIdx;

            // Slide the track so the active bar is centered in the visible window
            if (newIdx >= 0 && this.seqTrackEl && this.sequenceStripEl) {
                const visibleWidth = this.sequenceStripEl.clientWidth;
                const barCenterX = this.sequenceBarTimings[newIdx].centerX;
                const trackEl = this.seqTrackEl as HTMLElement & { _totalWidth?: number };
                const trackWidth = trackEl._totalWidth ?? 0;
                const maxTranslate = Math.max(0, trackWidth - visibleWidth);
                const targetTranslate = Math.max(0, Math.min(maxTranslate, barCenterX - visibleWidth / 2));
                this.seqTrackEl.style.transform = `translateX(-${targetTranslate}px)`;
            }
        }
    }

    /**
     * Remove the active highlight from all bar sub-blocks (called on stop).
     * Also resets the track scroll position to the beginning.
     */
    private clearSequenceHighlight(): void {
        if (this.lastActiveBarIdx >= 0 && this.lastActiveBarIdx < this.sequenceBarTimings.length) {
            this.sequenceBarTimings[this.lastActiveBarIdx].el.classList.remove('active');
        }
        this.lastActiveBarIdx = -1;
        // Reset track to start position (instant snap, no animation)
        if (this.seqTrackEl) {
            this.seqTrackEl.style.transition = 'none';
            void this.seqTrackEl.offsetWidth; // forced reflow
            this.seqTrackEl.style.transform = 'translateX(0)';
            requestAnimationFrame(() => {
                if (this.seqTrackEl) {
                    this.seqTrackEl.style.transition = '';
                }
            });
        }
    }

    private setPlayingState(isPlaying: boolean) {
        this.isPlaying = isPlaying;
        this.playBtn.disabled = !this.currentLoop;
        this.stopBtn.disabled = !isPlaying;
        this.increaseBtn.disabled = !isPlaying;
        this.decreaseBtn.disabled = !isPlaying;
        this.generateBtn.disabled = isPlaying;

        // Toggle play/pause icon
        this.playBtn.classList.toggle('fa-play', !isPlaying);
        this.playBtn.classList.toggle('fa-pause', isPlaying);
        this.playBtn.title = isPlaying ? 'Pause' : 'Play';

        // Clear all active states, then set the right one
        [this.playBtn, this.stopBtn, this.increaseBtn, this.decreaseBtn, this.generateBtn]
            .forEach(b => b.classList.remove('active'));
        if (isPlaying) {
            this.playBtn.classList.add('active');
        }
    }

    private setGeneratingState(isGenerating: boolean) {
        this.generateBtn.disabled = isGenerating;
        this.playBtn.disabled = isGenerating;
        if (isGenerating) {
            this.generateBtn.classList.add('generating');
            this.generateLabelEl.textContent = 'Generating...';
        } else {
            this.generateBtn.classList.remove('generating');
            this.generateLabelEl.innerHTML = 'Generate<br>Loop';
        }
    }



    private onSizeChange(size: SizeCategory) {
        this.selectedSizeCategory = size;

        // Update active state on size buttons
        this.sizeShortBtn.classList.remove('active');
        this.sizeMidBtn.classList.remove('active');
        this.sizeLargeBtn.classList.remove('active');
        this.sizeCustomBtn.classList.remove('active');

        if (size === 'SHORT') {
            this.sizeShortBtn.classList.add('active');
        } else if (size === 'MID') {
            this.sizeMidBtn.classList.add('active');
        } else if (size === 'LARGE') {
            this.sizeLargeBtn.classList.add('active');
        } else if (size === 'CUSTOM') {
            this.sizeCustomBtn.classList.add('active');
        }

        // Update LCD mode label immediately
        this.updateLcdMode(size);
    }

    private updateLcdMode(size: SizeCategory): void {
        const modeLabel = AudioLoopPlayer.SIZE_CATEGORY_DISPLAY_NAMES[size] ?? size;
        this.lcdModeEl.textContent = modeLabel;
    }

    private loadStyleGroups() {
        const styleNames = Object.keys(STYLE_GROUPS);
        const defaultStyle = styleNames.includes('Groove') ? 'Groove' : styleNames[0];

        // Populate hidden select for legacy compatibility
        this.genreSelect.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select a style...';
        this.genreSelect.appendChild(placeholder);
        for (const groupName of styleNames) {
            const option = document.createElement('option');
            option.value = groupName;
            option.textContent = groupName;
            this.genreSelect.appendChild(option);
        }
        this.genreSelect.value = defaultStyle;
        this.selectedStyleGroup = defaultStyle;

        // Store style names for index-based navigation
        this.lcdStyleNames = styleNames;
        this.currentStyleIndex = styleNames.indexOf(defaultStyle);
        if (this.currentStyleIndex < 0) this.currentStyleIndex = 0;

        // Set initial LCD display
        this.lcdStyleNameEl.textContent = defaultStyle;
        this.updateLcdMode(this.selectedSizeCategory);
        this.updateLcdNavButtons();

        // Enable touch-swipe on the LCD screen
        this.initLcdTouchSwipe();

        this.updateStatusLoopName();
        this.updateStatus('Click "Generate Loop" to create a loop', 'idle');
    }

    /** Add touch-swipe support to the LCD screen for mobile users. */
    private initLcdTouchSwipe(): void {
        const SWIPE_THRESHOLD_PX = 40;
        let touchStartX = 0;
        const lcdScreen = document.getElementById('lcdScreen');
        if (!lcdScreen) return;

        lcdScreen.addEventListener('touchstart', (e: TouchEvent) => {
            touchStartX = e.touches[0].clientX;
        }, { passive: true });

        lcdScreen.addEventListener('touchend', (e: TouchEvent) => {
            const deltaX = e.changedTouches[0].clientX - touchStartX;
            if (deltaX < -SWIPE_THRESHOLD_PX) {
                this.navigateLcd('next');
            } else if (deltaX > SWIPE_THRESHOLD_PX) {
                this.navigateLcd('prev');
            }
        }, { passive: true });
    }

    private onLcdStyleChange(groupName: string): void {
        // Changing style resets any pinned user album
        this.unpinAlbum();
        this.genreSelect.value = groupName;
        this.selectedStyleGroup = groupName;
        this.generateBtn.disabled = false;
        this.updateStatus('Click "Generate Loop" to create a loop', 'idle');
    }

    private navigateLcd(direction: 'prev' | 'next'): void {
        const total = this.lcdStyleNames.length;
        if (total === 0) return;

        if (direction === 'next') {
            this.currentStyleIndex = Math.min(this.currentStyleIndex + 1, total - 1);
        } else {
            this.currentStyleIndex = Math.max(this.currentStyleIndex - 1, 0);
        }

        const newStyle = this.lcdStyleNames[this.currentStyleIndex];
        this.onLcdStyleChange(newStyle);
        this.triggerLcdRefresh(newStyle);
        this.updateLcdNavButtons();
    }

    /** Trigger the digital refresh animation and update the style name text. */
    private triggerLcdRefresh(styleName: string): void {
        this.lcdContentEl.classList.remove('refreshing');
        // Force reflow so the animation restarts cleanly
        void this.lcdContentEl.offsetWidth;
        this.lcdStyleNameEl.textContent = styleName;
        this.lcdContentEl.classList.add('refreshing');
        setTimeout(() => {
            this.lcdContentEl.classList.remove('refreshing');
        }, 220);
    }

    private updateLcdNavButtons(): void {
        this.lcdPrevBtn.disabled = this.currentStyleIndex <= 0;
        this.lcdNextBtn.disabled = this.currentStyleIndex >= this.lcdStyleNames.length - 1;
    }

    private onGenreChange() {
        // Changing style resets any pinned user album
        this.unpinAlbum();
        const selectedGenre = this.genreSelect.value;
        if (selectedGenre) {
            this.selectedStyleGroup = selectedGenre;
            this.generateBtn.disabled = false;
            this.updateStatus('Click "Generate Loop" to create a loop', 'idle');
        } else {
            this.selectedStyleGroup = '';
            this.generateBtn.disabled = true;
            this.updateStatus('Choose a style and loop size to get started', 'idle');
        }
    }

    // ---------------------------------------------------------------------------
    // Custom builder
    // ---------------------------------------------------------------------------

    private openCustomBuilder(): void {
        // Gate: Custom mode is Pro-only. Free-tier users see an upgrade notice instead.
        if (this.currentUser && this.currentUser.tier === 'free') {
            this.updateStatus('Custom mode is available on Pro. Upgrade to build your own patterns.', 'error');
            // Snap back the active button state to the previously selected size
            this.onSizeChange(this.selectedSizeCategory === 'CUSTOM' ? 'SHORT' : this.selectedSizeCategory);
            return;
        }
        if (!this.customBuilderOverlay || !this.customBuilderSheet) return;
        this.customBuilderOverlay.classList.add('open');
        this.customBuilderSheet.classList.add('open');
        this._initBuilderInteractions();
        this._renderBuilderChips();
    }

    private closeCustomBuilder(): void {
        if (!this.customBuilderOverlay || !this.customBuilderSheet) return;
        this.customBuilderOverlay.classList.remove('open');
        this.customBuilderSheet.classList.remove('open');
    }

    /** Wire up builder interactions (idempotent — uses data-builder-wired flag) */
    private _initBuilderInteractions(): void {
        const sheet = this.customBuilderSheet;
        if (!sheet || sheet.dataset.builderWired) return;
        sheet.dataset.builderWired = '1';

        // Close button
        sheet.querySelector('#customBuilderCloseBtn')?.addEventListener('click', () => this.closeCustomBuilder());
        this.customBuilderOverlay?.addEventListener('click', () => this.closeCustomBuilder());

        // Section type buttons
        sheet.querySelectorAll<HTMLButtonElement>('.custom-section-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                sheet.querySelectorAll('.custom-section-btn').forEach(b => b.classList.remove('selected'));
                btn.classList.add('selected');
                this.builderSelectedSection = btn.dataset.section ?? 'verse';
            });
        });
        // Select verse by default
        const defaultSectionBtn = sheet.querySelector<HTMLButtonElement>('.custom-section-btn[data-section="verse"]');
        if (defaultSectionBtn) {
            defaultSectionBtn.classList.add('selected');
        }

        // Bar count buttons
        sheet.querySelectorAll<HTMLButtonElement>('.custom-bars-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                sheet.querySelectorAll('.custom-bars-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this.builderSelectedBars = parseInt(btn.dataset.bars ?? '8', 10);
            });
        });
        // Select 8 bars by default
        const defaultBarsBtn = sheet.querySelector<HTMLButtonElement>('.custom-bars-btn[data-bars="8"]');
        if (defaultBarsBtn) {
            defaultBarsBtn.classList.add('active');
            sheet.querySelector<HTMLButtonElement>('.custom-bars-btn[data-bars="4"]')?.classList.remove('active');
        }

        // Add section button
        sheet.querySelector('#customBuilderAddBtn')?.addEventListener('click', () => {
            this.customPattern.push({ [this.builderSelectedSection]: this.builderSelectedBars });
            this._renderBuilderChips();
        });

        // Clear button
        sheet.querySelector('#customBuilderClearBtn')?.addEventListener('click', () => {
            this.customPattern = [];
            this._renderBuilderChips();
        });

        // Confirm button
        this.customBuilderConfirmBtn?.addEventListener('click', () => {
            if (this.customPattern.length === 0) return;
            this.onSizeChange('CUSTOM');
            this._updateCustomBtnBadge();
            this.closeCustomBuilder();
        });
    }

    /** Re-render the chip list from this.customPattern */
    private _renderBuilderChips(): void {
        if (!this.customBuilderChips || !this.customBuilderHint || !this.customBuilderConfirmBtn) return;

        this.customBuilderChips.innerHTML = '';

        for (let i = 0; i < this.customPattern.length; i++) {
            const slot = this.customPattern[i];
            const section = Object.keys(slot)[0];
            const bars = slot[section];

            const chip = document.createElement('span');
            chip.className = `custom-chip seq-${section}`;
            chip.innerHTML = `${AudioLoopPlayer.TOKEN_ABBR[section] ?? section} <small>${bars}b</small> <span class="chip-remove">x</span>`;
            chip.addEventListener('click', () => {
                this.customPattern.splice(i, 1);
                this._renderBuilderChips();
            });
            this.customBuilderChips.appendChild(chip);
        }

        const hasChips = this.customPattern.length > 0;
        this.customBuilderHint.classList.toggle('hidden', hasChips);
        this.customBuilderConfirmBtn.disabled = !hasChips;
    }

    /** Update the chip count badge on the Custom button */
    private _updateCustomBtnBadge(): void {
        const count = this.customPattern.length;
        if (count > 0) {
            this.sizeCustomBtn.classList.add('custom-pattern-set');
            this.sizeCustomBtn.dataset.chipCount = String(count);
        } else {
            this.sizeCustomBtn.classList.remove('custom-pattern-set');
            delete this.sizeCustomBtn.dataset.chipCount;
        }
    }

    /**
     * Retry wrapper for generateNewPatternLoop.
     * Attempts up to maxAttempts times, logging each transient failure.
     * Only surfaces the error to the caller on the final failed attempt.
     */
    private async _generateWithRetry(
        genre: string,
        sizeCategory: SizeCategory,
        maxAttempts: number = 3,
    ): Promise<LoopResult> {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                return await generateNewPatternLoop(genre, sizeCategory, this.qdrantService);
            } catch (e) {
                if (attempt === maxAttempts) throw e;
                console.warn(`[WARN] Generation attempt ${attempt}/${maxAttempts} failed, retrying...`, e);
            }
        }
        // Unreachable — TypeScript requires a return/throw after the loop
        throw new Error('[WARN] _generateWithRetry: unreachable');
    }

    private async generateNewLoop() {
        // Track generate button click (before async — synchronous, captures intent)
        track('generate_clicked', {
            style_group: this.selectedStyleGroup,
            bar_size: this.selectedSizeCategory,
        }, this.currentUser?.id);

        this.playbackRate = 1.0;
        this.playbackOffset = 0;
        this.updateTempoDisplay();

        try {
            const selectedGenre = this.genreSelect.value;
            if (!selectedGenre) {
                throw new Error('No style selected');
            }

            // Daily generation cap check (free tier only, authenticated users only).
            // Pro users and anonymous users pass through immediately.
            if (this.currentUser) {
                const gate = await this.loopLibraryService.checkGenerateGate();
                if (gate && !gate.ok) {
                    const limitMsg = gate.limit ? `${gate.limit} generations/day on Free` : 'daily limit';
                    this.updateStatus(`Daily limit reached (${limitMsg}). Come back tomorrow or upgrade to Pro.`, 'error');
                    this.setGeneratingState(false);
                    return;
                }
            }

            // Sync selectedStyleGroup with the current UI selection
            this.selectedStyleGroup = selectedGenre;

            // Stop current playback if playing
            if (this.isPlaying) {
                this.stop();
            }

            this.updateStatus('Generating loop...', 'loading');
            this.setGeneratingState(true);

            const mode: GeneratorMode = 'Advanced';
            if (isDev) {
                console.log('[App] Generating', mode, 'loop for style group:', selectedGenre);
            }

            if (isDev) {
                console.log('[App] Using', this.selectedSizeCategory, 'size category');
            }

            if (this.pinnedAlbum) {
                // Pinned album always takes priority — generate from the user's album.
                // In CUSTOM mode, pass the custom pattern so the user's album is used
                // with their custom structure instead of a random album.
                const customPattern = this.selectedSizeCategory === 'CUSTOM'
                    ? (this.customPattern.length > 0 ? this.customPattern : undefined)
                    : undefined;
                if (this.selectedSizeCategory === 'CUSTOM' && this.customPattern.length === 0) {
                    throw new Error('No custom pattern defined. Please build a pattern first.');
                }
                this.currentLoop = await generateFromAlbum(
                    this.pinnedAlbum.librosa_album,
                    this.pinnedAlbum.bpm,
                    this.pinnedAlbum.genre,
                    this.selectedSizeCategory === 'CUSTOM' ? 'SHORT' : this.selectedSizeCategory,
                    this.qdrantService,
                    customPattern,
                );
            } else if (this.selectedSizeCategory === 'CUSTOM') {
                if (this.customPattern.length === 0) {
                    throw new Error('No custom pattern defined. Please build a pattern first.');
                }
                this.currentLoop = await generateCustomPatternLoop(
                    selectedGenre,
                    this.customPattern,
                    this.qdrantService,
                );
            } else {
                this.currentLoop = await this._generateWithRetry(selectedGenre, this.selectedSizeCategory);
            }

            // Clear old audio buffer - will be created in play()
            this.audioBuffer = null;

            // Calculate duration for display
            const sampleRate = this.currentLoop.sampleRate;
            const duration = this.currentLoop.audioData.length / sampleRate;
            if (isDev) {
                console.log('[App] Generated loop, duration:', duration, 'seconds');
            }

            // Track successful generation
            incrementLoopCount();
            track('loop_generated', {
                style_group: selectedGenre,
                bar_size: this.selectedSizeCategory,
                bpm: this.currentLoop.bpm,
                duration_seconds: parseFloat(duration.toFixed(2)),
                session_loop_index: loopCount,
            }, this.currentUser?.id);

            // Compute deterministic loop_id for "already saved" detection
            const tokens = this.currentLoop.segments.map(s => s.file);
            this.currentLoopId = await computeLoopId(
                this.currentLoop.album,
                tokens,
                this.currentLoop.bpm,
                this.currentLoop.totalDuration,
                this.selectedStyleGroup,
                this.selectedSizeCategory
            );

            // Update UI
            this.isLoadedFromLibrary = false;
            this.updateInfo(this.currentLoop, duration);
            this.renderSongPatternOverview(this.currentLoop);
            this.renderSequenceStrip(this.currentLoop);
            this.setGeneratingState(false);
            this.updateSaveBtnVisibility();
            this.updateSaveBtnState();
            this.updateStatusLoopName();
            if (!this.currentUser) this.showSaveNudge();

            // Attempt autoplay
            this.play();

            await new Promise(resolve => setTimeout(resolve, 80));
            if (this.audioContext && this.audioContext.state === 'suspended') {
                this.setPlayingState(false);
                this.updateStatus('Loop ready — tap Play to start', 'idle');
            }

        } catch (error) {
            if (isDev) { console.error('[App] Error generating loop:', error); }
            const errorMessage = (error as Error).message;

            // Track generation failure
            track('loop_generation_failed', {
                style_group: this.selectedStyleGroup,
                bar_size: this.selectedSizeCategory,
                error: errorMessage,
            }, this.currentUser?.id);
            
            // Check if it's a "no albums found" error for the entire style group
            if (errorMessage.includes('No albums found for style group')) {
                this.updateStatus('No albums found for this style. Please try another.', 'error');
            } else {
                this.updateStatus('Could not generate loop. Please try again.', 'error');
            }
            this.setGeneratingState(false);
        }
    }

    // Must be called synchronously inside a user gesture handler to satisfy iOS Web Audio unlock policy.
    private unlockAudioContext(): void {
        if (!this.audioContext) {
            this.audioContext = new AudioContext();
            this.analyserNode = this.audioContext.createAnalyser();
            this.analyserNode.fftSize = 512;
        }
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }
    }

    private play() {
        if (!this.currentLoop) {
            if (isDev) { console.error('[App] Cannot play: no loop generated'); }
            return;
        }

        // Stop any existing source before creating a new one.
        // Prevents ghost audio when play() is called twice (autoplay attempt + user tap on mobile).
        if (this.sourceNode) {
            this.sourceNode.onended = null;
            try { this.sourceNode.stop(); } catch { /* already stopped */ }
            this.sourceNode.disconnect();
            this.sourceNode = null;
        }

        // Create audio context if not exists (must be in user gesture handler)
        if (!this.audioContext) {
            this.audioContext = new AudioContext();
            if (isDev) {
                console.log('[App] Created AudioContext, sample rate:', this.audioContext.sampleRate);
            }
            
            // Create analyser node with fixed fftSize for consistent visualization
            this.analyserNode = this.audioContext.createAnalyser();
            this.analyserNode.fftSize = 512; // Balanced value for smooth yet dramatic bars
            if (isDev) {
                console.log('[App] Created AnalyserNode with fftSize:', this.analyserNode.fftSize);
            }
        }

        // Resume audio context if suspended
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        // Create audio buffer if not exists
        if (!this.audioBuffer) {
            const audio = this.currentLoop.audioData;
            this.audioBuffer = this.audioContext.createBuffer(
                audio.numberOfChannels,
                audio.length,
                this.currentLoop.sampleRate
            );
            for (let c = 0; c < audio.numberOfChannels; c++) {
                this.audioBuffer.getChannelData(c).set(audio.channels[c]);
            }
            if (isDev) {
                console.log('[App] Created AudioBuffer, channels:', audio.numberOfChannels, 'duration:', this.audioBuffer.duration, 'seconds');
            }
        }

        // Create source node
        this.sourceNode = this.audioContext.createBufferSource();
        this.sourceNode.buffer = this.audioBuffer;
        this.sourceNode.loop = true; // Enable looping
        this.sourceNode.playbackRate.value = this.playbackRate;
        
        // Add onended handler to prevent state desync
        this.sourceNode.onended = () => {
            if (this.isPlaying) {
                this.stop();
            }
        };

        // Connect: source -> analyser -> destination
        this.sourceNode.connect(this.analyserNode!);
        this.analyserNode!.connect(this.audioContext.destination);

        // Start playback from offset
        this.playbackStartTime = this.audioContext.currentTime;
        this.sourceNode.start(0, this.playbackOffset);
        if (isDev) {
            console.log('[App] Started audio loop at rate:', this.playbackRate, 'offset:', this.playbackOffset);
        }

        this.setPlayingState(true);
        this.updateStatus('Playing audio loop at', 'playing');

        // Track play event
        this.playStartTime = Date.now();
        track('loop_played', {
            style_group: this.selectedStyleGroup,
            bar_size: this.selectedSizeCategory,
            bpm: this.currentLoop.bpm,
        }, this.currentUser?.id);

        // Start visualizer
        this.startVisualizer();
    }

    private togglePlayPause() {
        if (this.isPlaying) {
            this.pause();
        } else {
            this.play();
        }
    }

    private pause() {
        if (!this.isPlaying || !this.audioContext || !this.audioBuffer || !this.sourceNode) return;

        // Save current position so resume starts from here
        const elapsed = this.audioContext.currentTime - this.playbackStartTime;
        this.playbackOffset = (this.playbackOffset + elapsed * this.playbackRate) % this.audioBuffer.duration;

        this.sourceNode.onended = null;
        this.sourceNode.stop();
        this.sourceNode.disconnect();
        this.sourceNode = null;

        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        this.canvasContext.clearRect(0, 0, window.innerWidth, window.innerHeight);
        this.clearSequenceHighlight();

        this.setPlayingState(false);
        this.updateStatus('Paused', 'idle');
    }

    stop() {
        // Track stop event (only if we were actually playing)
        if (this.isPlaying && this.playStartTime > 0) {
            track('loop_stopped', {
                play_duration_seconds: parseFloat(((Date.now() - this.playStartTime) / 1000).toFixed(2)),
            }, this.currentUser?.id);
        }

        if (this.sourceNode) {
            this.sourceNode.stop();
            this.sourceNode.disconnect();
            this.sourceNode = null;
            if (isDev) {
                console.log('[App] Stopped audio loop');
            }
        }

        // Stop visualizer
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        // Clear canvas (use logical dimensions)
        this.canvasContext.clearRect(0, 0, window.innerWidth, window.innerHeight);

        // Clear sequence strip highlight
        this.clearSequenceHighlight();

        // Reset playback offset
        this.playbackOffset = 0;

        this.setPlayingState(false);
        this.updateStatus('Audio stopped', 'idle');
    }

    private increaseRate() {
        if (!this.isPlaying || !this.audioContext || !this.audioBuffer) {
            return;
        }

        // Calculate current playback position
        const elapsed = this.audioContext.currentTime - this.playbackStartTime;
        this.playbackOffset = (this.playbackOffset + elapsed * this.playbackRate) % this.audioBuffer.duration;

        // Increase rate (clamp precision to avoid float drift)
        this.playbackRate = parseFloat((this.playbackRate * 1.01).toFixed(4));
        this.updateTempoDisplay();

        // Restart playback with new rate
        this.restartPlayback();
        track('tempo_changed', { direction: 'up', new_rate: this.playbackRate });
        if (isDev) {
            console.log('[App] Increased playback rate to:', this.playbackRate);
        }
    }

    private decreaseRate() {
        if (!this.isPlaying || !this.audioContext || !this.audioBuffer) {
            return;
        }

        // Calculate current playback position
        const elapsed = this.audioContext.currentTime - this.playbackStartTime;
        this.playbackOffset = (this.playbackOffset + elapsed * this.playbackRate) % this.audioBuffer.duration;

        // Decrease rate (clamp precision to avoid float drift)
        this.playbackRate = parseFloat((this.playbackRate / 1.01).toFixed(4));
        this.updateTempoDisplay();

        // Restart playback with new rate
        this.restartPlayback();
        track('tempo_changed', { direction: 'down', new_rate: this.playbackRate });
        if (isDev) {
            console.log('[App] Decreased playback rate to:', this.playbackRate);
        }
    }

    private restartPlayback() {
        if (!this.audioContext || !this.audioBuffer || !this.analyserNode) {
            return;
        }

        // Stop current source (remove onended handler first to prevent unwanted stop() call)
        if (this.sourceNode) {
            this.sourceNode.onended = null;
            this.sourceNode.stop();
            this.sourceNode.disconnect();
        }

        // Create new source with updated rate
        this.sourceNode = this.audioContext.createBufferSource();
        this.sourceNode.buffer = this.audioBuffer;
        this.sourceNode.loop = true;
        this.sourceNode.playbackRate.value = this.playbackRate;
        
        // Add onended handler to prevent state desync
        this.sourceNode.onended = () => {
            if (this.isPlaying) {
                this.stop();
            }
        };

        // Connect: source -> analyser -> destination
        this.sourceNode.connect(this.analyserNode);
        this.analyserNode.connect(this.audioContext.destination);

        // Start from current offset
        this.playbackStartTime = this.audioContext.currentTime;
        this.sourceNode.start(0, this.playbackOffset);
    }

    private updateThemeIcon(btn: HTMLButtonElement, theme: string): void {
        const icon = btn.querySelector('i');
        if (!icon) return;
        if (theme === 'dark') {
            icon.className = 'fas fa-sun';
            btn.title = 'Switch to light mode';
        } else {
            icon.className = 'fas fa-moon';
            btn.title = 'Switch to dark mode';
        }
    }

    private startVisualizer() {
        if (!this.analyserNode) {
            return;
        }

        const bufferLength = this.analyserNode.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
            if (!this.isPlaying || !this.analyserNode) {
                return;
            }

            this.animationFrameId = requestAnimationFrame(draw);

            // Update sequence strip highlight (O(n) scan, DOM only on bar change)
            this.updateSequenceHighlight();

            // Read brightness floor from CSS variable (allows live theme switching)
            const brightnessStr = getComputedStyle(document.documentElement)
                .getPropertyValue('--viz-min-brightness').trim();
            const brightnessFloor = parseInt(brightnessStr || '60', 10);

            // Get frequency data
            this.analyserNode.getByteFrequencyData(dataArray);

            // Clear canvas (use logical dimensions after scale)
            const logicalWidth = window.innerWidth;
            const logicalHeight = window.innerHeight;
            this.canvasContext.clearRect(0, 0, logicalWidth, logicalHeight);

            // Calculate bar dimensions - snap split to integer to avoid subpixel seam
            const splitY = Math.floor(logicalHeight * 0.45);
            const barWidth = (logicalWidth / bufferLength) * 1.618;
            let x = 0;

            // Draw frequency bars with red/magenta/blue gradient (no yellow/green)
            // Draw both top bars AND mirrored reflection in single pass (no temp canvas = no seam)
            for (let i = 0; i < bufferLength; i++) {
                const barHeight = dataArray[i];
                // Use screen position ratio instead of buffer index ratio for consistent colors across screen sizes
                const screenRatio = Math.min(1, x / logicalWidth);
                // Red-to-magenta-to-blue gradient with brightness floor from theme token
                const maxRange = 255 - brightnessFloor;
                const c = brightnessFloor + Math.min(maxRange, maxRange * screenRatio * 2.4);
                const g = 30 + 20 * screenRatio;
                const u = brightnessFloor + Math.min(maxRange, (barHeight + 20 * screenRatio) * 1.4);
                
                // TOP BAR with very subtle bottom fade for blend effect
                const topGradient = this.canvasContext.createLinearGradient(0, splitY - barHeight, 0, splitY);
                topGradient.addColorStop(0, `rgb(${c}, ${g}, ${u})`);
                topGradient.addColorStop(0.92, `rgb(${c}, ${g}, ${u})`);
                topGradient.addColorStop(1, `rgba(${c}, ${g}, ${u}, 0.9)`);
                this.canvasContext.fillStyle = topGradient;
                this.canvasContext.fillRect(x, splitY - barHeight, barWidth, barHeight);
                
                // MIRRORED BAR (bottom) - draw directly with fade (larger than upper bars)
                const mirrorHeight = barHeight * 1.2; // 20% larger than upper bars
                const gradient = this.canvasContext.createLinearGradient(0, splitY, 0, splitY + mirrorHeight);
                gradient.addColorStop(0, `rgba(${c}, ${g}, ${u}, 0.5)`);
                gradient.addColorStop(1, `rgba(${c}, ${g}, ${u}, 0.0)`);
                this.canvasContext.fillStyle = gradient;
                this.canvasContext.fillRect(x, splitY, barWidth, mirrorHeight);
                
                x += barWidth + 1;
            }
        };

        draw();
    }
}

// ---------------------------------------------------------------------------
// TOS Modal
// ---------------------------------------------------------------------------

/**
 * Show the Terms of Service modal before the first upload.
 * Returns true if the user accepted, false if they cancelled.
 * If the user has already accepted (localStorage key present), resolves true immediately.
 */
export async function showTosModal(): Promise<boolean> {
    if (localStorage.getItem('looperc_tos_accepted')) return true;

    const backdrop = document.getElementById('tosModalBackdrop') as HTMLElement;
    const checkbox = document.getElementById('tosCheckbox') as HTMLInputElement;
    const acceptBtn = document.getElementById('tosAcceptBtn') as HTMLButtonElement;
    const cancelBtn = document.getElementById('tosCancelBtn') as HTMLButtonElement;

    // Reset state
    checkbox.checked = false;
    acceptBtn.disabled = true;

    // Show modal
    backdrop.classList.add('visible');

    // Focus the modal for accessibility
    const modal = document.getElementById('tosModal') as HTMLElement;
    modal.focus();

    // Wire up checkbox to enable/disable accept button
    const onCheckboxChange = () => {
        acceptBtn.disabled = !checkbox.checked;
    };
    checkbox.addEventListener('change', onCheckboxChange);

    // Escape key closes modal (cancel)
    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            cleanup();
            resolve(false);
        }
    };
    document.addEventListener('keydown', onKeyDown);

    let resolve!: (value: boolean) => void;

    const cleanup = () => {
        backdrop.classList.remove('visible');
        checkbox.removeEventListener('change', onCheckboxChange);
        document.removeEventListener('keydown', onKeyDown);
        acceptBtn.onclick = null;
        cancelBtn.onclick = null;
    };

    return new Promise<boolean>((res) => {
        resolve = res;

        acceptBtn.onclick = () => {
            localStorage.setItem('looperc_tos_accepted', Date.now().toString());
            cleanup();
            res(true);
        };

        cancelBtn.onclick = () => {
            cleanup();
            res(false);
        };
    });
}
